// Analytics — admin-only AI dashboard builder.
//
// Two modes (segmented switch in the header):
//   • Builder    — type a question, the AI returns a chart/table spec + data;
//                  tweak chart type / axes client-side; save to a dashboard.
//   • Dashboards — gallery of saved dashboards + a canvas of widgets. Each
//                  widget re-runs its FROZEN sql via /run (no LLM). Dashboards
//                  shared with you appear read-only.
//
// All shared UI comes from window.* (primitives, WidgetRenderer, AtlasApi).
// Hooks are destructured module-locally (each source file is its own module
// scope under esbuild — see app.jsx / company_detail.jsx for the same pattern).

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const _err = (e) => {
  const raw = (e && (e.message || e.detail)) || 'Something went wrong';
  // API errors arrive as "<status> <statusText>: <json body>" — surface the
  // server's human-readable `detail` and drop the status/JSON wrapper.
  const i = typeof raw === 'string' ? raw.indexOf('{') : -1;
  if (i !== -1) {
    try { const d = JSON.parse(raw.slice(i)); if (d && d.detail) return String(d.detail); } catch (_) { /* not JSON */ }
  }
  return raw;
};

// Visual identity for suggestion categories. Icons are resolved by NAME at
// render time (window.Icon may not exist yet at module-eval under esbuild).
const CATEGORY_META = {
  pipeline:  { label: 'Pipeline',        icon: 'Pipeline', hue: 'accent' },
  companies: { label: 'Companies',       icon: 'Building', hue: 'teal'   },
  team:      { label: 'Team & activity', icon: 'Users',    hue: 'violet' },
  fees:      { label: 'Fees & value',    icon: 'Money',    hue: 'green'  },
  explore:   { label: 'Explore',         icon: 'Sparkle',  hue: 'amber'  },
};
const HUE_BG = { accent: 'var(--accent-soft)', teal: 'var(--teal-bg)', violet: 'var(--violet-bg)', green: 'var(--green-bg)', amber: 'var(--amber-bg)', slate: 'var(--slate-bg)' };
const HUE_FG = { accent: 'var(--accent-text)', teal: 'var(--teal-fg)', violet: 'var(--violet-fg)', green: 'var(--green-fg)', amber: 'var(--amber-fg)', slate: 'var(--slate-fg)' };
const iconByName = (name, fallback = 'Grid') => (window.Icon && (window.Icon[name] || window.Icon[fallback])) || (() => null);

// KPI-tile number formatting (compact, rounded — no float artifacts).
const fmtStat = (v, format) => {
  const n = Number(v) || 0;
  if (format === 'currency') {
    if (Math.abs(n) >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
    if (Math.abs(n) >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
    if (Math.abs(n) >= 1e3) return '$' + Math.round(n / 1e3) + 'K';
    return '$' + Math.round(n).toLocaleString();
  }
  if (format === 'percent') return Math.round(n) + '%';
  return Math.round(n).toLocaleString();
};

// Tiny seeded shuffle so "Shuffle" reorders the idea pool deterministically per
// click without a network round-trip (the AI/catalog pool is fetched once).
const _hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return h; };
// XOR each item's stable hash with a per-seed constant. (Mixing the seed into
// the string tail only shifts every hash by the same amount → identical order;
// XOR with a per-seed key actually permutes the ordering.)
const shuffleSeeded = (arr, seed) => {
  const k = Math.imul(seed + 1, 0x9e3779b1) | 0;
  return [...arr]
    .map((x, i) => [x, (_hash((x.title || '') + ':' + i) ^ k) >>> 0])
    .sort((a, b) => a[1] - b[1])
    .map((p) => p[0]);
};

// Friendly source label: drop the `v_` view prefix, then humanize the
// snake_case identifier the same way columns are — "v_active_engagements"
// reads as "Active engagements", not as a raw table name.
const humanizeSource = (name) => (window.humanizeCol ? window.humanizeCol(String(name == null ? '' : name).replace(/^v_/, '')) : String(name == null ? '' : name));
// Idea-card meta reads "<chart type> · <source>" (e.g. "Bar · v_active_engagements").
// Humanize the source half — including bare single-word tables like "activities" —
// and leave the chart-type label alone. With no separator, fall back to rewriting
// any inline snake_case identifier.
const humanizeMeta = (text) => {
  const s = String(text == null ? '' : text);
  const i = s.lastIndexOf('·');
  if (i === -1) return s.replace(/[a-z][a-z0-9]*(?:_[a-z0-9]+)+/g, (m) => humanizeSource(m));
  return s.slice(0, i + 1) + ' ' + humanizeSource(s.slice(i + 1).trim());
};

// Column list for the server-export dialog: prefer the spec's curated
// table_columns (their keys + labels), else humanize the raw result columns.
const exportColumns = (spec, resultCols) => {
  const tc = spec && Array.isArray(spec.table_columns) && spec.table_columns.length ? spec.table_columns : null;
  if (tc) return tc.map((c) => ({ key: c.key, label: c.label || window.humanizeCol(c.key) }));
  return (resultCols || []).map((c) => ({ key: c, label: window.humanizeCol(c) }));
};

const CONFIDENCE_HUE = { high: 'green', medium: 'slate', low: 'amber' };
const VISIBILITY_LABEL = { private: 'Private', team: 'Team', users: 'Shared', org: 'Everyone' };
const VISIBILITY_HUE = { private: 'slate', team: 'teal', users: 'violet', org: 'green' };

// ── Assumptions + caveats disclosure (the trust surface) ─────────────────────
const AssumptionsDisclosure = ({ spec, warnings }) => {
  const [open, setOpen] = useState(true);
  const assumptions = (spec && spec.assumptions) || [];
  const caveats = (spec && spec.caveats) || [];
  if (!assumptions.length && !caveats.length && !(warnings && warnings.length)) return null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--accent-soft)', overflow: 'hidden' }}>
      <button onClick={() => setOpen((v) => !v)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
        border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
      }}>
        <window.Icon.Sparkle size={13} color="var(--accent-text)" />
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', flex: 1 }}>
          What the AI assumed{caveats.length ? ` · ${caveats.length} caveat${caveats.length === 1 ? '' : 's'}` : ''}
        </span>
        <window.Icon.ChevronR size={11} color="var(--accent-text)" style={{ transform: open ? 'rotate(90deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{ padding: '0 12px 10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {assumptions.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
          {[...caveats, ...((warnings || []).filter((w) => !caveats.includes(w)))].map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11.5, color: 'var(--amber-fg)', background: 'var(--amber-bg)', padding: '6px 8px', borderRadius: 5 }}>
              <span>⚠</span><span>{c}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Spec editor — client-side re-mapping of an existing result ───────────────
const SpecEditor = ({ spec, setSpec, columns }) => {
  const colOpts = [{ value: '', label: '— none —' }, ...columns.map((c) => ({ value: c, label: window.humanizeCol ? window.humanizeCol(c) : c }))];
  const set = (patch) => setSpec((s) => ({ ...s, ...patch }));
  const isChart = !['kpi', 'table'].includes(spec.chart_type);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <label style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
        <div style={{ marginBottom: 3 }}>Chart</div>
        <window.Combobox value={spec.chart_type} onChange={(e) => set({ chart_type: e.target.value })}
          options={window.CHART_TYPES} style={{ width: 150 }} />
      </label>
      {isChart && (
        <>
          <label style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
            <div style={{ marginBottom: 3 }}>{spec.chart_type === 'scatter' ? 'X (numeric)' : 'Category (X)'}</div>
            <window.Combobox value={spec.x || ''} onChange={(e) => set({ x: e.target.value || null })}
              options={colOpts} style={{ width: 140 }} />
          </label>
          <label style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
            <div style={{ marginBottom: 3 }}>Measure (Y)</div>
            <window.Combobox value={(spec.y && spec.y[0]) || ''} onChange={(e) => set({ y: e.target.value ? [e.target.value] : null })}
              options={colOpts} style={{ width: 140 }} />
          </label>
          {!['pie', 'donut', 'scatter'].includes(spec.chart_type) && (
            <label style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
              <div style={{ marginBottom: 3 }}>Split by</div>
              <window.Combobox value={spec.series || ''} onChange={(e) => set({ series: e.target.value || null })}
                options={colOpts} style={{ width: 130 }} />
            </label>
          )}
        </>
      )}
      <label style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
        <div style={{ marginBottom: 3 }}>Format</div>
        <window.Combobox value={spec.value_format || 'number'} onChange={(e) => set({ value_format: e.target.value })}
          options={[{ value: 'number', label: 'Number' }, { value: 'currency', label: 'Currency' }, { value: 'percent', label: 'Percent' }, { value: 'count', label: 'Count' }, { value: 'duration_days', label: 'Days' }]}
          style={{ width: 120 }} />
      </label>
    </div>
  );
};

// ── Landing building blocks (overview · ideas · recents · sources) ──────────
const SectionHead = ({ icon, title, sub, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}>
    {icon}
    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
    {sub && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{sub}</span>}
    <div style={{ flex: 1 }} />
    {action}
  </div>
);

// Shared empty/zero-state — soft icon medallion + title + sub + optional CTA.
const EmptyState = ({ icon = 'Grid', title, sub, action }) => {
  const C = iconByName(icon, 'Grid');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '52px 24px', textAlign: 'center' }}>
      <span style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <C size={22} />
      </span>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-3)', maxWidth: 380, lineHeight: 1.5 }}>{sub}</div>}
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
    </div>
  );
};

// KPI overview strip ---------------------------------------------------------
const StatTile = ({ s }) => {
  const C = iconByName(s.icon, 'Activity');
  const hue = s.hue || 'accent';
  const hasDelta = s.delta_pct != null;
  const up = s.delta_dir !== 'down';
  const hasSpark = Array.isArray(s.series) && s.series.length > 1;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px' }} title={s.hint || ''}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: HUE_BG[hue], color: HUE_FG[hue], display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><C size={13} /></span>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }} className="num">{fmtStat(s.value, s.format)}</div>
          {hasDelta && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10.5, fontWeight: 600, marginTop: 3, color: up ? 'var(--green-fg)' : 'var(--rose-fg)' }}>
              {up ? <window.Icon.ArrowUp size={10} /> : <window.Icon.ArrowDn size={10} />}{Math.abs(s.delta_pct)}%
              <span style={{ color: 'var(--text-4)', fontWeight: 500, marginLeft: 2 }}>vs prev</span>
            </div>
          )}
        </div>
        {hasSpark && <window.Sparkline data={s.series} color={HUE_FG[hue]} w={62} h={hasDelta ? 30 : 22} />}
      </div>
    </div>
  );
};

const OverviewStrip = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    window.AtlasApi.analytics.overview().then((s) => setStats(Array.isArray(s) ? s : [])).catch(() => setStats([]));
  }, []);
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };
  if (stats === null) return <div style={grid}>{[0, 1, 2, 3].map((i) => <window.Skeleton key={i} w="100%" h={72} style={{ borderRadius: 'var(--radius-lg)' }} />)}</div>;
  if (!stats.length) return null;
  return <div style={grid}>{stats.map((s) => <StatTile key={s.key} s={s} />)}</div>;
};

// Categorized idea cards -----------------------------------------------------
const IdeaCard = ({ item, disabled, onPick }) => {
  const meta = CATEGORY_META[item._cat] || CATEGORY_META.explore;
  const CatIcon = iconByName(meta.icon, 'Sparkle');
  const ChartIcon = (window.Icon && window.Icon.forChart) ? window.Icon.forChart(item.chart_hint) : iconByName('ChartBar');
  const [hover, setHover] = useState(false);
  return (
    <button onClick={() => onPick(item)} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 11, textAlign: 'left', width: '100%',
        padding: 13, borderRadius: 'var(--radius-lg)', cursor: disabled ? 'default' : 'pointer',
        background: 'var(--surface)', border: '1px solid ' + (hover && !disabled ? 'var(--border-strong)' : 'var(--border)'),
        boxShadow: hover && !disabled ? 'var(--shadow-sm)' : 'none', transition: 'border-color .12s, box-shadow .12s', opacity: disabled ? 0.55 : 1,
      }}>
      <span style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 9, background: HUE_BG[meta.hue], color: HUE_FG[meta.hue], display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CatIcon size={16} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{item.title}</span>
          {item.origin === 'ai' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 9, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', borderRadius: 4, padding: '1px 4px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
              <window.Icon.Sparkle size={8} /> AI
            </span>
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
          <ChartIcon size={12} color="var(--text-4)" />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{humanizeMeta(item.description)}</span>
        </span>
      </span>
      <window.Icon.Arrow size={13} color={hover && !disabled ? 'var(--accent-text)' : 'var(--text-4)'} style={{ marginTop: 2, flexShrink: 0 }} />
    </button>
  );
};

const IdeaGrid = ({ disabled, onPick }) => {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState('all');
  const [seed, setSeed] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  useEffect(() => {
    window.AtlasApi.analytics.suggest().then(setData).catch(() => setData({ categories: [], generated_by: 'schema' }));
  }, []);
  // Explicit opt-in to the (cached, audited) LLM call — keeps the landing fast
  // and the spend governed. Only offered when the backend says it's available.
  const loadAI = () => {
    setAiLoading(true);
    window.AtlasApi.analytics.suggest({ ai: true, refresh: true })
      .then((d) => { setData(d); setSeed((s) => s + 1); })
      .catch(() => {})
      .finally(() => setAiLoading(false));
  };

  const allItems = useMemo(() => ((data && data.categories) || []).flatMap((c) => c.items.map((i) => ({ ...i, _cat: c.key }))), [data]);
  const cats = useMemo(() => ((data && data.categories) || []).map((c) => ({ key: c.key, label: c.label, n: c.items.length })), [data]);
  const shown = useMemo(() => {
    const base = cat === 'all' ? allItems : allItems.filter((i) => i._cat === cat);
    return shuffleSeeded(base, seed).slice(0, 9);
  }, [allItems, cat, seed]);

  if (data === null) {
    return (
      <div>
        <SectionHead icon={<window.Icon.Bulb size={14} color="var(--text-3)" />} title="Suggested ideas" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 10 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => <window.Skeleton key={i} w="100%" h={62} style={{ borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      </div>
    );
  }
  if (!allItems.length) return null;

  const chip = (key, label, n) => {
    const on = cat === key;
    return (
      <button key={key} onClick={() => setCat(key)} style={{
        fontSize: 11.5, padding: '3px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
        border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
        background: on ? 'var(--accent-soft)' : 'var(--surface)', color: on ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: on ? 600 : 500,
      }}>{label}{n != null ? <span style={{ opacity: 0.55, marginLeft: 4 }}>{n}</span> : null}</button>
    );
  };

  return (
    <div>
      <SectionHead
        icon={<window.Icon.Bulb size={14} color="var(--accent)" />}
        title="Suggested ideas"
        action={<div style={{ display: 'flex', gap: 6 }}>
          {data.ai_available && (
            <window.Btn size="sm" variant="outline" disabled={aiLoading} icon={<window.Icon.Sparkle size={12} />} onClick={loadAI}>
              {aiLoading ? 'Thinking…' : 'AI ideas'}
            </window.Btn>
          )}
          <window.Btn size="sm" variant="ghost" icon={<window.Icon.Shuffle size={12} />} onClick={() => setSeed((s) => s + 1)}>Shuffle</window.Btn>
        </div>}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {chip('all', 'All', allItems.length)}
        {cats.map((c) => chip(c.key, c.label, c.n))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 10 }}>
        {shown.map((item, i) => <IdeaCard key={item.title + i} item={item} disabled={disabled} onPick={onPick} />)}
      </div>
    </div>
  );
};

// Recent dashboards ----------------------------------------------------------
const RecentDashboards = ({ onOpen }) => {
  const [dashes, setDashes] = useState(null);
  useEffect(() => {
    window.AtlasApi.analytics.listDashboards().then((d) => setDashes(Array.isArray(d) ? d : [])).catch(() => setDashes([]));
  }, []);
  const recent = useMemo(() => [...(dashes || [])].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)).slice(0, 6), [dashes]);
  if (!dashes || !recent.length) return null;
  return (
    <div>
      <SectionHead icon={<window.Icon.Clock size={14} color="var(--text-3)" />} title="Jump back in" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {recent.map((d) => (
          <button key={d.id} onClick={() => onOpen && onOpen(d.id)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', maxWidth: 280,
          }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}>
            <window.Icon.Grid size={13} color="var(--accent)" />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <span style={{ fontSize: 10.5, color: 'var(--text-4)', flexShrink: 0 }}>{d.widget_count} widget{d.widget_count === 1 ? '' : 's'}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Data-source catalog --------------------------------------------------------
const SourceCatalog = ({ disabled, onPick }) => {
  const [sources, setSources] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    window.AtlasApi.analytics.sources().then((s) => setSources(Array.isArray(s) ? s : [])).catch(() => setSources([]));
  }, []);
  if (!sources || !sources.length) return null;
  const sorted = [...sources].sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0));
  const shown = open ? sorted : sorted.slice(0, 8);
  return (
    <div>
      <SectionHead icon={<window.Icon.Database size={14} color="var(--text-3)" />} title="Your data"
        sub={`· ${sources.length} source${sources.length === 1 ? '' : 's'}`} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {shown.map((s) => {
          const label = humanizeSource(s.name);
          const ncol = (s.columns || []).length;
          return (
            <button key={s.name} disabled={disabled} title={`${s.name} · ${ncol} columns — click to start a question`}
              onClick={() => onPick && onPick(`Summarize the key metrics in ${label}`)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 999,
                border: '1px solid var(--border)', background: s.recommended ? 'var(--accent-soft)' : 'var(--surface-2)',
                cursor: disabled ? 'default' : 'pointer', color: s.recommended ? 'var(--accent-text)' : 'var(--text-2)',
              }}>
              {s.recommended && <window.Icon.Spark size={10} />}
              <span style={{ fontSize: 11.5 }}>{label}</span>
              <span style={{ opacity: 0.55, fontSize: 10.5 }}>{ncol}</span>
            </button>
          );
        })}
        {sorted.length > 8 && (
          <button onClick={() => setOpen((v) => !v)} style={{ fontSize: 11, color: 'var(--accent-text)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>
            {open ? 'Show less' : `+${sorted.length - 8} more`}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Drill-down inspector: click a chart element → see the data behind it ─────
// `api` overrides the drill/detail endpoints (the Views page passes its
// any-role mirrors); defaults to the admin analytics ones.
const DrillInspector = ({ spec, data, selection, onClose, crossFilterCols, onCrossFilter, dashId, widgetId, filters, api, zIndex }) => {
  const A = api || window.AtlasApi.analytics;
  // Build a drill request from a structured query (manual builds) OR an AI
  // `drill` block — either names a source + base filters; we add the clicked
  // dimension as an equality filter. Null → no source-record drill available.
  const drillReq = (() => {
    const sel = selection || {};
    if (sel.value == null) return null;
    const q = spec && spec.query;
    if (q && q.source) return { source: q.source, filters: q.filters || [], column: sel.dimension, value: sel.value };
    const dr = spec && spec.drill;
    if (dr && dr.source) return { source: dr.source, filters: dr.filters || [], column: dr.dimension_column || sel.dimension, value: sel.value };
    return null;
  })();
  const canDrill = !!(drillReq && drillReq.column);

  // A curated widget can carry a `detail` query: $name placeholders bound to the
  // clicked row's group-by values → the underlying records (e.g. the call notes
  // with their companies/contacts), even when the grouping is a computed bucket
  // (a week) that the equality-only /drill can't express.
  const detailSpec = (spec && spec.detail && spec.detail.sql) ? spec.detail : null;
  const useDetail = !!detailSpec;
  const detailParams = detailSpec ? (() => {
    const sel = selection || {};
    const out = {};
    for (const name of (detailSpec.params || [])) {
      if (sel.row && name in sel.row) out[name] = sel.row[name];
      else if (name === sel.dimension) out[name] = sel.value;
      else if (name === sel.seriesKey) out[name] = sel.seriesValue;
      else out[name] = null;
    }
    return out;
  })() : null;
  const [records, setRecords] = useState(null);
  const [recErr, setRecErr] = useState('');
  const [showAll, setShowAll] = useState(false);
  const fmt = (selection && selection.format) || (spec && spec.value_format) || 'number';
  const U = window.AnalyticsChartUtils || { fmtVal: (v) => String(v) };
  const H = (s) => (window.humanizeCol ? window.humanizeCol(s) : s);
  const recEntity = U.sourceEntity ? U.sourceEntity(drillReq && drillReq.source) : null;
  const dataEntity = U.sourceEntity ? U.sourceEntity((spec && spec.query && spec.query.source) || (spec && spec.drill && spec.drill.source)) : null;

  useEffect(() => {
    const fail = (e) => { setRecErr(_err(e)); setRecords({ columns: [], rows: [] }); };
    if (useDetail) {
      setRecords(null); setRecErr('');
      // Saved widget → widget-scoped endpoint that inherits the active dashboard
      // filters (server reuses the stored detail SQL + filterable). Unsaved
      // preview (no widget id) → the generic detail endpoint.
      const req = (dashId && widgetId)
        ? window.AtlasApi.analytics.widgetDetail(dashId, widgetId, { params: detailParams, filters: filters || [], limit: 200 })
        : A.detail({ sql: detailSpec.sql, params: detailParams, limit: 200 });
      req.then(setRecords).catch(fail);
      return;
    }
    if (!canDrill) return;
    setRecords(null); setRecErr('');
    A.drill({ ...drillReq, limit: 200 }).then(setRecords).catch(fail);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const chips = [];
  if (useDetail && detailParams) {
    for (const [k, v] of Object.entries(detailParams)) if (v != null) chips.push([H(k), String(v)]);
  } else {
    if (selection.dimension && selection.value != null) chips.push([H(selection.dimension), String(selection.value)]);
    if (selection.seriesKey && selection.seriesValue != null) chips.push([H(selection.seriesKey), String(selection.seriesValue)]);
    if (selection.measure && selection.measureValue != null) chips.push([H(selection.measure), U.fmtVal(selection.measureValue, fmt)]);
  }

  // Dashboard + widget filters the drill inherited — only those this widget can
  // push down (i.e. declared in `filterable`) actually constrain the records.
  const inheritedFilters = (useDetail && (dashId && widgetId) && Array.isArray(filters) && filters.length)
    ? filters.filter((f) => (spec.filterable || []).some((fc) => fc && fc.key === f.column))
        .map((f) => ({ label: H(f.column), value: Array.isArray(f.value) ? f.value.join(', ') : String(f.value) }))
    : [];

  // Click-to-filter: offer to set a matching dashboard filter from the clicked
  // value(s). Only select/text filters — setting a number/date to one exact
  // clicked value is rarely what you want. Deduped by column.
  const xfButtons = (() => {
    if (!onCrossFilter || !crossFilterCols) return [];
    const pairs = [];
    if (selection.dimension && selection.value != null) pairs.push([selection.dimension, selection.value]);
    if (selection.seriesKey && selection.seriesValue != null) pairs.push([selection.seriesKey, selection.seriesValue]);
    if (selection.row) for (const [k, v] of Object.entries(selection.row)) if (v != null) pairs.push([k, v]);
    const seen = new Set(); const out = [];
    for (const [c, v] of pairs) {
      const def = crossFilterCols[c];
      if (!def || !['select', 'text'].includes(def.type) || seen.has(c)) continue;
      seen.add(c); out.push({ column: c, value: v, label: def.label });
    }
    return out;
  })();

  // "Open as table": hand the drill's source + filters to the Views builder —
  // a full-page, sortable/editable record list of these rows, ready to save as
  // a view. Only for equality drills (a detail-SQL drill has no structured
  // source to rebuild from).
  const openAsTable = (!useDetail && canDrill && window.AtlasNav && window.AtlasNav.openViewBuilder) ? () => {
    const flt = [...(drillReq.filters || [])];
    if (drillReq.column && drillReq.value != null) flt.push({ column: drillReq.column, op: '=', value: drillReq.value });
    // The list compiler accepts up to 24 columns (query_compiler.
    // compile_list_query) — pass what fits, and say so when columns dropped.
    const allCols = ((records && records.columns) || []).filter((c) => c !== '__id' && !/(^id$|_id$)/i.test(c));
    const cols = allCols.slice(0, 24);
    const dropped = allCols.length - cols.length;
    if (dropped > 0 && window.pushToast) {
      window.pushToast({ type: 'info', title: `${dropped} column${dropped === 1 ? '' : 's'} beyond the builder's 24-column limit ${dropped === 1 ? 'was' : 'were'} dropped` });
    }
    window.AtlasNav.openViewBuilder({ source: drillReq.source, filters: flt, columns: cols, title: spec.title || '' });
    onClose();
  } : null;

  const allCols = (data && data.columns) || selection.columns || [];
  const colObjs = (cols) => (cols || []).map((c) => ({ key: c, label: c }));
  const behind = (selection.rows && selection.rows.length) ? selection.rows : (data ? data.rows : []);
  const tableBox = { border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' };
  const sectionHead = (icon, label, count, csv) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      {icon}
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
      {count != null && <span style={{ fontSize: 11, color: 'var(--text-4)' }} className="num">{count}</span>}
      <div style={{ flex: 1 }} />
      {csv}
    </div>
  );

  return (
    <window.Drawer open onClose={onClose} title="Underlying data" zIndex={zIndex}
      subtitle={selection.dimension && selection.value != null ? `${H(selection.dimension)} = ${selection.value}` : (spec.title || 'Details')}
      width={560} footer={<window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>}>

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {chips.map(([k, v], i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '3px 9px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
              <span style={{ color: 'var(--text-3)' }}>{k}</span><strong>{v}</strong>
            </span>
          ))}
        </div>
      )}

      {inheritedFilters.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-4)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <window.Icon.Filter size={10} /> Filters:
          </span>
          {inheritedFilters.map((c, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 9px', borderRadius: 999, border: '1px dashed var(--border-strong)', color: 'var(--text-3)' }}>
              {c.label}: <strong style={{ color: 'var(--text-2)' }}>{c.value}</strong>
            </span>
          ))}
        </div>
      )}

      {xfButtons.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Filter dashboard:</span>
          {xfButtons.map((b, i) => (
            <button key={i} onClick={() => { onCrossFilter(b.column, b.value); onClose(); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '3px 9px', borderRadius: 999,
                border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}>
              <window.Icon.Filter size={10} /> {b.label}: <strong>{String(b.value)}</strong>
            </button>
          ))}
        </div>
      )}

      {(useDetail || canDrill) ? (
        <div>
          {sectionHead(
            <window.Icon.List size={13} color="var(--text-3)" />, 'Source records',
            records && records.rows ? `${records.row_count != null ? records.row_count : records.rows.length}${records.truncated ? '+' : ''}` : null,
            records && records.rows && records.rows.length > 0 ? (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                {openAsTable && (
                  <window.Btn size="xs" variant="outline" icon={<window.Icon.Table size={11} />}
                    onClick={openAsTable} title="Open these records as a full-page table you can refine and save">Open as table</window.Btn>
                )}
                <window.Btn size="xs" variant="outline" icon={<window.Icon.External size={11} />}
                  onClick={() => window.exportCsv(`${(spec.title || 'records').replace(/[^\w]+/g, '_')}.csv`, colObjs(records.columns), records.rows)}>CSV</window.Btn>
              </span>
            ) : null,
          )}
          {records === null ? <window.SkeletonRows rows={4} />
            : recErr ? <div style={{ fontSize: 12, color: 'var(--rose-fg)' }}>{recErr}</div>
              : records.rows && records.rows.length ? (
                <div style={tableBox}><window.DataTableWidget columns={colObjs(records.columns)} rows={records.rows} valueFormat={fmt} pageSize={25} entityHint={recEntity} hideIds /></div>
              ) : <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No matching records.</div>}
        </div>
      ) : (
        <div>
          {sectionHead(
            <window.Icon.List size={13} color="var(--text-3)" />, 'Data behind this',
            `${behind.length} row${behind.length === 1 ? '' : 's'}`,
            <window.Btn size="xs" variant="outline" icon={<window.Icon.External size={11} />}
              onClick={() => window.exportCsv(`${(spec.title || 'data').replace(/[^\w]+/g, '_')}.csv`, colObjs(allCols), behind)}>CSV</window.Btn>,
          )}
          <div style={tableBox}><window.DataTableWidget columns={colObjs(allCols)} rows={behind} valueFormat={fmt} pageSize={25} entityHint={dataEntity} /></div>
          {data && data.rows && behind.length < data.rows.length && (
            <>
              <button onClick={() => setShowAll((v) => !v)} style={{ marginTop: 10, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>
                {showAll ? 'Hide full dataset' : `View full dataset (${data.rows.length} rows)`}
              </button>
              {showAll && <div style={{ marginTop: 8, ...tableBox }}><window.DataTableWidget columns={colObjs(allCols)} rows={data.rows} valueFormat={fmt} pageSize={25} entityHint={dataEntity} /></div>}
            </>
          )}
        </div>
      )}
    </window.Drawer>
  );
};

// ── Status-aware generate error panel ────────────────────────────────────────
// `error` is {status, message} — the status parsed off the raw apiFetch error
// ("<status> <statusText>: {json}") before _err strips it. One compact panel,
// message-driven variants: budget/rate 429s, transient 503s (retryable), and
// unprocessable 422s (rephrase hint).
const GenerateError = ({ error, onRetry }) => {
  const msg = error.message || 'Something went wrong';
  let title, hint, canRetry = false;
  if (error.status === 429) {
    title = /budget/i.test(msg) ? 'Daily AI budget reached' : 'Rate limited — wait a minute';
  } else if (error.status === 503) {
    title = 'The AI service is unavailable right now';
    canRetry = true;
  } else if (error.status === 422) {
    title = "Couldn't build that.";
    hint = 'Try rephrasing, or be more specific about the metric and time range.';
  } else {
    title = 'Something went wrong.';
    canRetry = true;
  }
  return (
    <div style={{ maxWidth: 720, width: '100%', margin: '0 auto', background: 'var(--rose-bg)', color: 'var(--rose-fg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <strong>{title}</strong> {msg}
          {hint && <div style={{ marginTop: 4, color: 'var(--text-3)' }}>{hint}</div>}
        </div>
        {canRetry && onRetry && (
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Refresh size={12} />} onClick={onRetry}>Retry</window.Btn>
        )}
      </div>
    </div>
  );
};

// ── Question history (per-user, from /analytics/history) ────────────────────
// Ghost button + dropdown by the hero input. Picking an entry prefills the
// question; entries with a frozen spec replay through /run — no LLM cost.
const HistoryMenu = ({ entries, onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const trunc = (s) => { const t = String(s || ''); return t.length > 80 ? t.slice(0, 79) + '…' : t; };
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <window.Btn size="sm" variant="ghost" icon={<window.Icon.Clock size={12} />} onClick={() => setOpen((v) => !v)}>History</window.Btn>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 30, zIndex: 30, width: 380, maxWidth: '80vw', maxHeight: 320, overflow: 'auto', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 7, boxShadow: 'var(--shadow-lg)', textAlign: 'left' }}>
          {entries.map((h) => (
            <button key={h.id} onClick={() => { setOpen(false); onPick(h); }}
              title={h.spec ? 'Re-run this result' : 'Prefill this question'}
              style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', padding: '8px 11px', border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.35 }}>{trunc(h.question)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: 'var(--text-4)', width: '100%' }}>
                {h.success
                  ? <window.Pill hue={CONFIDENCE_HUE[h.confidence] || 'slate'} sz="sm">✓ {h.confidence || 'ok'}</window.Pill>
                  : <window.Pill hue="rose" sz="sm">✗ failed</window.Pill>}
                <span>{window.relativeTime(h.created_at)}</span>
                {h.surface === 'views' && <span>· views</span>}
                {h.feedback === 1 && <window.Icon.ThumbUp size={11} color="var(--green-fg)" />}
                {h.feedback === -1 && <window.Icon.ThumbDn size={11} color="var(--rose-fg)" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Thumbs up/down on a generated result (writes /history/{id}/feedback) ────
const FeedbackButtons = ({ queryId, initial }) => {
  const [val, setVal] = useState(initial === 1 ? 'up' : initial === -1 ? 'down' : null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const rate = (rating) => {
    setVal(rating);
    setNoteOpen(rating === 'down');
    window.AtlasApi.analytics.feedback(queryId, { rating }).catch(() => {});
  };
  const sendNote = () => {
    const n = note.trim();
    setNoteOpen(false);
    if (n) window.AtlasApi.analytics.feedback(queryId, { rating: 'down', note: n }).catch(() => {});
  };
  const btn = (rating, IconC, title) => {
    const on = val === rating;
    const fg = rating === 'up' ? 'var(--green-fg)' : 'var(--rose-fg)';
    const bg = rating === 'up' ? 'var(--green-bg)' : 'var(--rose-bg)';
    return (
      <button onClick={() => rate(rating)} title={title} style={{
        border: '1px solid ' + (on ? fg : 'var(--border)'), background: on ? bg : 'transparent',
        color: on ? fg : 'var(--text-4)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center',
      }}><IconC size={12} /></button>
    );
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {btn('up', window.Icon.ThumbUp, 'Good result')}
      {btn('down', window.Icon.ThumbDn, 'Something was wrong')}
      {noteOpen && (
        <input autoFocus value={note} onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') sendNote(); if (e.key === 'Escape') setNoteOpen(false); }}
          placeholder="What was wrong? (optional · Enter)"
          style={{ width: 200, fontSize: 11, padding: '3px 7px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
      )}
    </span>
  );
};

// A list of records — a table that isn't an aggregate. Those, plus empty
// answers, get checked against the data after the fast answer shows.
const isRecordList = (spec) => !!(spec && spec.chart_type === 'table' && spec.sql
  && !/\bgroup\s+by\b/i.test(spec.sql));

if (typeof document !== 'undefined' && !document.getElementById('atlas-analytics-spin')) {
  const el = document.createElement('style');
  el.id = 'atlas-analytics-spin';
  el.textContent = '@keyframes atlas-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(el);
}

// The line above a result while the list builder checks it, then what came
// of it.
const CheckStrip = ({ checking, note }) => {
  const box = {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '8px 12px', marginBottom: 10,
    border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-2)',
  };
  if (checking) {
    return (
      <div style={box} role="status" aria-live="polite">
        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent',
          display: 'inline-block', flexShrink: 0, animation: 'atlas-spin 0.9s linear infinite' }} />
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>Checking against the data</span>
        <span style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{checking.label}</span>
      </div>
    );
  }
  if (note && note.error) {
    return <div style={box}><window.Icon.Help size={13} /><span>Couldn’t check this list: {note.error}</span></div>;
  }
  const changed = note.before != null && note.before !== note.after;
  return (
    <div style={box}>
      <window.Icon.Check size={13} color="var(--green-fg)" />
      <span style={{ fontWeight: 600, color: 'var(--text)' }}>Checked against the data</span>
      <span className="num">{(note.after || 0).toLocaleString()} rows{changed ? ` · first pass had ${note.before.toLocaleString()}` : ''}</span>
    </div>
  );
};

// ── Builder ──────────────────────────────────────────────────────────────────
const Builder = ({ targetDashboardId, targetGroup, onSavedToDashboard, onOpenDashboard }) => {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  // The checked list (app/list_builder.py) that follows a fast answer which
  // came back empty, failed, or is a list of records: {label, before} while
  // it runs; then checkNote {before, after} | {error}.
  const [checking, setChecking] = useState(null);
  const [checkNote, setCheckNote] = useState(null);
  const runSeq = useRef(0);                       // a newer ask drops stale checks
  const [error, setError] = useState(null);      // {status, message} | null
  const [result, setResult] = useState(null);   // {spec, columns, rows, warnings, cost_usd, query_id}
  const [spec, setSpec] = useState(null);        // editable copy
  const [refineText, setRefineText] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [drill, setDrill] = useState(null);
  const [history, setHistory] = useState([]);
  const taRef = useRef(null);
  const lastRunRef = useRef(null);               // last generate args, for Retry

  // Auto-grow the ask box: one line by default, expands with the text so a
  // long question is readable without scrolling inside a single row. Caps at
  // ~10 lines, after which the textarea scrolls.
  const PROMPT_MAX_H = 260;
  useEffect(() => {
    const el = taRef.current;
    if (!el || !el.clientWidth) return;           // not laid out yet — a 0-width measure wraps everything
    el.style.height = 'auto';
    const h = Math.min(el.scrollHeight, PROMPT_MAX_H);
    el.style.height = h + 'px';
    el.style.overflowY = el.scrollHeight > PROMPT_MAX_H ? 'auto' : 'hidden';
  }, [prompt, result, generating]);

  const loadHistory = useCallback(() => {
    window.AtlasApi.analytics.history(20)
      .then((h) => setHistory(Array.isArray(h) ? h : []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Keep the leading HTTP status apiFetch prefixes onto errors ("<status>
  // <statusText>: {json}") so the panel can pick the right variant; _err
  // extracts the human-readable detail.
  const fail = (e) => {
    const raw = String((e && (e.message || e.detail)) || '');
    const m = /^(\d{3})\b/.exec(raw);
    setError({ status: m ? +m[1] : 0, message: _err(e) });
  };

  // Fast answer first (one SQL query, seconds). When it comes back empty,
  // fails, or is a list of records, the list builder checks it against the
  // data while the fast answer stays on screen, then replaces it.
  const run = async (request, baseSpec) => {
    if (!request || !request.trim()) return;
    lastRunRef.current = { request, baseSpec };
    const seq = ++runSeq.current;
    setGenerating(true); setError(null); setChecking(null); setCheckNote(null);
    let res = null;
    try {
      res = await window.AtlasApi.analytics.generate({
        request: request.trim(),
        ...(baseSpec ? { base_spec: baseSpec } : {}),
      });
      if (seq !== runSeq.current) return;
      setResult(res);
      setSpec(res.spec);
      setRefineText('');
    } catch (e) {
      if (seq !== runSeq.current) return;
      const m = /^(\d{3})\b/.exec(String((e && e.message) || ''));
      // Only a question the fast path couldn't answer is worth checking —
      // not a budget (429) or configuration (503) problem.
      if (baseSpec || !m || +m[1] !== 422) { fail(e); return; }
    } finally {
      if (seq === runSeq.current) setGenerating(false);
      loadHistory();   // both outcomes appended a history row
    }
    if (!baseSpec && (!res || res.row_count === 0 || isRecordList(res.spec))) {
      check(request, seq, res ? res.row_count : null);
    }
  };

  const check = async (request, seq, before) => {
    setChecking({ label: 'Planning the checks…', before });
    try {
      const res = await window.AtlasApi.analytics.buildListStream({ request: request.trim() }, (label) => {
        if (seq === runSeq.current) setChecking((c) => (c ? { ...c, label } : c));
      });
      if (seq !== runSeq.current) return;
      setResult(res);
      setSpec(res.spec);
      setCheckNote({ before, after: res.total_count != null ? res.total_count : res.row_count });
    } catch (e) {
      if (seq !== runSeq.current) return;
      if (before == null) fail(e);
      else setCheckNote({ error: _err(e) });
    } finally {
      if (seq === runSeq.current) setChecking(null);
      loadHistory();
    }
  };

  // Replay a history entry: prefill the question; a frozen spec re-runs via
  // /run (deterministic, no LLM cost) — same result shape minus cost fields.
  const replay = async (h) => {
    runSeq.current += 1; setChecking(null); setCheckNote(null);
    setPrompt(h.question);
    if (!h.spec || !h.spec.sql) { if (taRef.current) taRef.current.focus(); return; }
    setGenerating(true); setError(null);
    try {
      const res = await window.AtlasApi.analytics.run({ spec: h.spec });
      setResult({
        spec: h.spec, columns: res.columns, rows: res.rows, row_count: res.row_count,
        truncated: res.truncated, warnings: [], query_id: h.id, _feedback: h.feedback,
      });
      setSpec(h.spec);
      setRefineText('');
    } catch (e) {
      fail(e);
    } finally {
      setGenerating(false);
    }
  };

  const onPromptKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); run(prompt); }
  };
  // Idea cards PREFILL the question (same as the source-catalog starters) so
  // the user can tweak before spending an LLM call — no more auto-run.
  const pick = (item) => { const p = typeof item === 'string' ? item : item.prompt; useStarter(p); };
  const useStarter = (text) => { setPrompt(text); if (taRef.current) taRef.current.focus(); };
  const reset = () => {
    runSeq.current += 1;
    setResult(null); setSpec(null); setError(null); setChecking(null); setCheckNote(null);
  };

  const landing = !result && !generating;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22, padding: '20px 16px 30px', overflow: 'auto', flex: 1 }}>
      {landing && <OverviewStrip />}

      {/* Hero prompt — the single ask box. */}
      {!result && (
        <div style={{ maxWidth: 720, width: '100%', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--accent-text)', fontWeight: 600, marginBottom: 10 }}>
            <window.Icon.Sparkle size={13} /> AI analytics
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text)', marginBottom: 16, fontFamily: 'var(--font-display)' }}>What do you want to know?</h2>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end', textAlign: 'left',
            border: '1px solid ' + (focused ? 'var(--accent)' : 'var(--border-strong)'),
            boxShadow: focused ? '0 0 0 3px var(--accent-soft)' : 'var(--shadow-sm)',
            borderRadius: 'var(--radius-lg)', background: 'var(--surface)', padding: 8, transition: 'border-color .12s, box-shadow .12s',
          }}>
            <window.Icon.Sparkle size={16} color="var(--text-4)" style={{ margin: '9px 2px 0 6px', flexShrink: 0 }} />
            <textarea ref={taRef} value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={onPromptKey}
              onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} rows={1}
              placeholder="e.g. won deals by dealmaker this quarter"
              style={{ flex: 1, padding: '7px 4px', fontSize: 14, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', lineHeight: 1.45, resize: 'none', minHeight: 34, overflowY: 'hidden', boxSizing: 'border-box' }} />
            <window.Btn variant="accent" size="md" disabled={generating || !!checking || !prompt.trim()} onClick={() => run(prompt)}
              icon={<window.Icon.Sparkle size={13} />}>{generating ? 'Building…' : 'Generate'}</window.Btn>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 7, minHeight: 24 }}>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Press ⌘↵ to generate.</span>
            <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              {history.length > 0 && <HistoryMenu entries={history} onPick={replay} />}
            </span>
          </div>
        </div>
      )}

      {/* Landing blocks */}
      {landing && (
        <>
          <IdeaGrid disabled={generating} onPick={pick} />
          <RecentDashboards onOpen={onOpenDashboard} />
          <SourceCatalog disabled={generating} onPick={useStarter} />
        </>
      )}

      {/* Loading skeleton */}
      {generating && (
        <div style={{ maxWidth: 980, width: '100%', margin: '0 auto' }}>
          <window.Section>
            <div style={{ padding: 16 }}>
              <window.Skeleton w="40%" h={16} />
              <div style={{ marginTop: 12 }}><window.Skeleton w="100%" h={240} style={{ borderRadius: 8 }} /></div>
            </div>
          </window.Section>
        </div>
      )}

      {/* Checking with nothing on screen yet (the fast answer failed). */}
      {checking && !result && !generating && (
        <div style={{ maxWidth: 980, width: '100%', margin: '0 auto' }}>
          <CheckStrip checking={checking} />
        </div>
      )}

      {/* Error */}
      {error && !generating && (
        <GenerateError error={error}
          onRetry={() => lastRunRef.current && run(lastRunRef.current.request, lastRunRef.current.baseSpec)} />
      )}

      {/* Result */}
      {result && spec && !generating && (
        <div style={{ maxWidth: 980, width: '100%', margin: '0 auto' }}>
          <div style={{ marginBottom: 10 }}>
            <window.Btn size="sm" variant="ghost" icon={<window.Icon.ChevronL size={13} />} onClick={reset}>New question</window.Btn>
          </div>
          {(checking || checkNote) && <CheckStrip checking={checking} note={checkNote} />}
          <window.Section>
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* title + meta */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input value={spec.title || ''} onChange={(e) => setSpec((s) => ({ ...s, title: e.target.value }))}
                  style={{ flex: 1, minWidth: 200, fontSize: 15, fontWeight: 600, color: 'var(--text)', border: '1px solid transparent', borderRadius: 5, padding: '3px 6px', background: 'transparent' }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border)'}
                  onBlur={(e) => e.target.style.borderColor = 'transparent'} />
                {spec.confidence && <window.Pill hue={CONFIDENCE_HUE[spec.confidence] || 'slate'} sz="sm">{spec.confidence} confidence</window.Pill>}
                <span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">
                  {result.row_count} row{result.row_count === 1 ? '' : 's'}{result.truncated ? '+' : ''}
                </span>
                {result.query_id && (
                  <FeedbackButtons key={result.query_id} queryId={result.query_id} initial={result._feedback} />
                )}
                <window.Btn size="sm" variant="outline" icon={<window.Icon.External size={12} />}
                  onClick={() => setCsvOpen(true)} title="Export as CSV — up to 10,000 rows">
                  CSV
                </window.Btn>
                <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => setSaveOpen(true)}>
                  Save
                </window.Btn>
              </div>

              {/* preview */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 12, minHeight: 200 }}>
                <window.WidgetRenderer spec={spec} data={result} height={spec.chart_type === 'table' ? undefined : 320}
                  onSelect={(sel) => setDrill({ spec, data: result, selection: sel })} />
                <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--text-4)', textAlign: 'center' }}>Tip: click any point to see the data behind it.</div>
              </div>

              <SpecEditor spec={spec} setSpec={setSpec} columns={result.columns} />
              <AssumptionsDisclosure spec={spec} warnings={result.warnings} />

              {/* refine */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <window.Icon.Sparkle size={13} color="var(--text-3)" />
                <input value={refineText} onChange={(e) => setRefineText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && refineText.trim()) run(refineText, spec); }}
                  placeholder="Refine with AI — e.g. “only top 5” or “make it a line chart by month”"
                  style={{ flex: 1, padding: '6px 9px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
                <window.Btn size="sm" variant="outline" disabled={!refineText.trim()} onClick={() => run(refineText, spec)}>Refine</window.Btn>
              </div>
            </div>
          </window.Section>
        </div>
      )}

      {saveOpen && (
        <SaveToDashboardDialog
          open={saveOpen}
          onClose={() => setSaveOpen(false)}
          spec={spec}
          sourceRequest={prompt}
          defaultDashboardId={targetDashboardId}
          defaultGroup={targetGroup}
          onSaved={(dashId) => { setSaveOpen(false); onSavedToDashboard && onSavedToDashboard(dashId); }}
        />
      )}

      {csvOpen && spec && (
        <window.ExportDialog title={spec.title || 'Analytics result'}
          columns={exportColumns(spec, result && result.columns)}
          onClose={() => setCsvOpen(false)}
          onExport={(cols) => window.AtlasApi.analytics.exportCsv({ sql: spec.sql, title: spec.title, columns: cols || undefined })} />
      )}

      {drill && <DrillInspector spec={drill.spec} data={drill.data} selection={drill.selection} onClose={() => setDrill(null)} />}
    </div>
  );
};

// ── Save dialog ──────────────────────────────────────────────────────────────
const SaveToDashboardDialog = ({ open, onClose, spec, sourceRequest, defaultDashboardId, defaultGroup, onSaved }) => {
  const [dashboards, setDashboards] = useState(null);
  const [choice, setChoice] = useState(defaultDashboardId || '__new__');
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState(defaultGroup || '');
  const [visibility, setVisibility] = useState('private');
  const [title, setTitle] = useState(spec?.title || 'Untitled');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.AtlasApi.analytics.listDashboards()
      .then((d) => {
        const editable = (Array.isArray(d) ? d : []).filter((x) => x.can_edit);
        setDashboards(editable);
        if (!defaultDashboardId) setChoice(editable.length ? editable[0].id : '__new__');
      })
      .catch(() => setDashboards([]));
  }, []);

  // Existing group names, for quick-pick chips when creating a dashboard.
  const groupChips = React.useMemo(() => {
    const s = new Set();
    (dashboards || []).forEach((d) => { if (d.group_name) s.add(d.group_name); });
    return [...s].sort();
  }, [dashboards]);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let dashId = choice;
      if (choice === '__new__') {
        const created = await window.AtlasApi.analytics.createDashboard({
          name: newName.trim() || 'New dashboard', visibility,
          group_name: newGroup.trim() || null,
        });
        dashId = created.id;
      }
      await window.AtlasApi.analytics.createWidget(dashId, {
        title: title.trim() || spec.title || 'Untitled',
        spec, source_request: sourceRequest || null,
      });
      window.pushToast({ type: 'success', title: 'Saved to dashboard' });
      onSaved(dashId);
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Save failed', body: _err(e) });
    } finally { setBusy(false); }
  };

  return (
    <window.Drawer open={open} onClose={onClose} title="Save to dashboard" width={440}
      footer={<>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={save} disabled={busy || (choice === '__new__' && !newName.trim())}>
          {busy ? 'Saving…' : 'Save'}
        </window.Btn>
      </>}>
      <window.Field label="Widget title">
        <window.TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
      </window.Field>
      <window.Field label="Dashboard">
        {dashboards === null ? <window.Skeleton w="100%" h={30} /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dashboards.map((d) => (
              <RadioRow key={d.id} selected={choice === d.id} onClick={() => setChoice(d.id)}
                title={d.name} sub={`${d.group_name ? d.group_name + ' · ' : ''}${d.widget_count} widget${d.widget_count === 1 ? '' : 's'} · ${VISIBILITY_LABEL[d.visibility]}`} />
            ))}
            <RadioRow selected={choice === '__new__'} onClick={() => setChoice('__new__')}
              title="Create new dashboard" sub="Start a fresh dashboard for this widget" />
          </div>
        )}
      </window.Field>
      {choice === '__new__' && (
        <>
          <window.Field label="New dashboard name">
            <window.TextInput value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Pipeline overview" autoFocus />
          </window.Field>
          <window.Field label="Group / page (optional)" hint="Blank = Ungrouped. Type a new name to create a group.">
            <window.TextInput value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="e.g. Sales" />
            {groupChips.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                {groupChips.map((g) => (
                  <button key={g} onClick={() => setNewGroup(g)} style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 999, cursor: 'pointer',
                    border: '1px solid ' + (newGroup === g ? 'var(--accent)' : 'var(--border)'),
                    background: newGroup === g ? 'var(--accent-soft)' : 'var(--surface-2)',
                    color: newGroup === g ? 'var(--accent-text)' : 'var(--text-2)',
                  }}>{g}</button>
                ))}
              </div>
            )}
          </window.Field>
          <window.Field label="Visibility">
            <VisibilityPicker value={visibility} onChange={setVisibility} compact />
          </window.Field>
        </>
      )}
    </window.Drawer>
  );
};

const RadioRow = ({ selected, onClick, title, sub }) => (
  <button onClick={onClick} role="radio" aria-checked={selected} style={{
    display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', padding: '8px 10px', textAlign: 'left',
    background: selected ? 'var(--accent-soft)' : 'var(--surface)', cursor: 'pointer',
    border: '1px solid ' + (selected ? 'var(--accent)' : 'var(--border)'), borderRadius: 7,
  }}>
    <span style={{ width: 14, height: 14, borderRadius: 999, marginTop: 1, flexShrink: 0, border: '1.5px solid', borderColor: selected ? 'var(--accent)' : 'var(--border-strong)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {selected && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />}
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{sub}</div>}
    </div>
  </button>
);

const VisibilityPicker = ({ value, onChange }) => {
  const opts = [
    ['private', 'Private', 'Only you can see it'],
    ['team', 'My team', 'Everyone on your team (read-only)'],
    ['users', 'Specific people', 'Pick people — optionally let them edit'],
    ['org', 'Everyone', 'All Atlas users (read-only)'],
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {opts.map(([v, t, s]) => <RadioRow key={v} selected={value === v} onClick={() => onChange(v)} title={t} sub={s} />)}
    </div>
  );
};

// ── Share dialog ─────────────────────────────────────────────────────────────
const ShareDialog = ({ open, onClose, dashboard, onSaved }) => {
  const [visibility, setVisibility] = useState(dashboard.visibility);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(() => new Set([
    ...(dashboard.shared_user_ids || []),
    ...((dashboard.shares || []).map((s) => s.user_id)),
  ]));
  // Per-user can_edit grants (only meaningful when visibility='users').
  const [editors, setEditors] = useState(() => new Set(
    (dashboard.shares || []).filter((s) => s.can_edit).map((s) => s.user_id)
  ));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (visibility !== 'users' || members.length) return;
    window.AtlasApi.listTeamMembers().then((m) => setMembers(Array.isArray(m) ? m.filter((u) => u.active !== false) : [])).catch(() => setMembers([]));
  }, [visibility]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleEdit = (id) => setEditors((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setBusy(true);
    try {
      const ids = visibility === 'users' ? [...selected] : [];
      const updated = await window.AtlasApi.analytics.shareDashboard(dashboard.id, {
        visibility,
        shared_user_ids: ids,   // legacy mirror (view-only) for back-compat
        entries: ids.map((id) => ({ user_id: id, can_edit: editors.has(id) })),
      });
      window.pushToast({ type: 'success', title: 'Sharing updated' });
      onSaved(updated);
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Update failed', body: _err(e) });
    } finally { setBusy(false); }
  };

  return (
    <window.Drawer open={open} onClose={onClose} title="Share dashboard" subtitle={dashboard.name} width={420}
      footer={<>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save sharing'}</window.Btn>
      </>}>
      <VisibilityPicker value={visibility} onChange={setVisibility} />
      {visibility === 'users' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 6 }}>People</div>
          <div style={{ maxHeight: 280, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {members.map((m) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, fontSize: 12.5, color: 'var(--text)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', minWidth: 0 }}>
                  <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} style={{ accentColor: 'var(--accent)' }} />
                  <window.Avatar name={m.full_name} size={20} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.full_name}</span>
                </label>
                {selected.has(m.id) && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--text-3)', cursor: 'pointer', flexShrink: 0 }}>
                    <input type="checkbox" checked={editors.has(m.id)} onChange={() => toggleEdit(m.id)} style={{ accentColor: 'var(--accent)' }} />
                    can edit
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </window.Drawer>
  );
};

// ── Widget card (owns its own data via the cheap /run path) ──────────────────
// At most this many widget /run requests in flight at once. Every card on a
// dashboard mounts (and "Refresh all" re-runs) simultaneously; unbounded,
// a 10-widget dashboard stampedes the analytics DB connections server-side
// and stalls every other API request behind it. Extras queue FIFO here.
const RUN_MAX_INFLIGHT = 4;
let _runInflight = 0;
const _runWaiters = [];
async function limitedRun(fn) {
  while (_runInflight >= RUN_MAX_INFLIGHT) {
    await new Promise((resolve) => _runWaiters.push(resolve));
  }
  _runInflight += 1;
  try {
    return await fn();
  } finally {
    _runInflight -= 1;
    const next = _runWaiters.shift();
    if (next) next();
  }
}

// Column span for a widget on the 4-col canvas grid (persisted in layout.w;
// KPIs default to a quarter, charts to a half).
const widgetSpan = (w) => Math.min(4, Math.max(1, (w.layout && w.layout.w) || ((w.spec && w.spec.chart_type === 'kpi') ? 1 : 2)));
// Row span (persisted in layout.h, 1-3) → the widget body's render height.
const widgetRows = (w) => Math.min(3, Math.max(1, (w.layout && w.layout.h) || 1));
const WIDGET_HEIGHT = { 1: 240, 2: 500, 3: 760 };

// ── Per-widget viewer filters ────────────────────────────────────────────────
// Field candidates for a widget's filter popover: the spec's declared
// `filterable` defs when present; otherwise the last run's result columns with
// categories guessed from the first non-null value (mirrors the saved-views
// fallback — the server post-filters on output columns).
const widgetFilterCols = (spec, data) => {
  const fl = Array.isArray(spec.filterable) ? spec.filterable.filter((fc) => fc && fc.key) : [];
  if (fl.length) return fl.map((fc) => ({ name: fc.key, category: ['number', 'date', 'boolean'].includes(fc.type) ? fc.type : 'text' }));
  if (!data || !data.columns) return [];
  const rows = data.rows || [];
  return data.columns.map((name) => {
    const v = rows.map((r) => r[name]).find((x) => x != null);
    return { name, category: (typeof v === 'number') ? 'number' : 'text' };
  });
};
const _wfH = (s) => (window.humanizeCol ? window.humanizeCol(s) : String(s == null ? '' : s));
// Chip text for one active wire filter ("Stage in list Won, Lost").
const wfChipText = (f) => {
  const op = OP_LABEL[f.op] || f.op;
  if (f.op === 'is_null' || f.op === 'is_not_null') return `${_wfH(f.column)} ${op}`;
  const v = Array.isArray(f.value) ? f.value.join(f.op === 'between' ? ' – ' : ', ') : String(f.value);
  return `${_wfH(f.column)} ${op} ${v.length > 40 ? v.slice(0, 39) + '…' : v}`;
};

// Funnel popover on each widget card: FilterRow-style rows (shared with the
// saved-views page via window.*), applied on top of the dashboard-level
// filters for this widget only. `value`/`defaults` are wire-shape filters.
const WidgetFilterPopover = ({ spec, cols, value, defaults, canEdit, onApply, onSaveDefault, onClose }) => {
  const [rowsState, setRowsState] = useState(() => window.filtersToRows(value || []));
  const popRef = useRef(null);
  useEffect(() => {
    // Combobox menus are portaled to <body> — a click in one lands outside
    // popRef, so don't treat it as a click-away (same as window.FilterPopover).
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)
          && !(e.target.closest && e.target.closest('[data-combobox-menu]'))) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const colCat = Object.fromEntries(cols.map((c) => [c.name, c.category]));
  // Value pickers honor each filterable def's source/value_column via the
  // /values endpoint; columns without one reject → free-text fallback.
  const fcMap = useMemo(() => Object.fromEntries(
    (Array.isArray(spec.filterable) ? spec.filterable : []).filter((fc) => fc && fc.key).map((fc) => [fc.key, fc])
  ), [spec]);
  const fetchValues = useCallback((_src, col) => {
    const fc = fcMap[col];
    return (fc && fc.source && fc.value_column)
      ? window.AtlasApi.analytics.values(fc.source, fc.value_column)
      : Promise.reject(new Error('no value picker'));
  }, [fcMap]);

  const apply = (wire) => { onApply(wire); onClose(); };
  const hasDefaults = Array.isArray(defaults) && defaults.length > 0;
  const diverged = JSON.stringify(value || []) !== JSON.stringify(defaults || []);

  return (
    <div ref={popRef} style={{ position: 'absolute', right: 0, top: 24, zIndex: 30, width: 300, background: 'var(--surface)',
      border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Widget filters</span>
        <div style={{ flex: 1 }} />
        {hasDefaults && diverged && (
          <button onClick={() => apply(defaults)} title="Back to the filters saved on this widget"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--accent-text)', padding: 0 }}>Reset to default</button>
        )}
      </div>
      {cols.length === 0 ? (
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginBottom: 8 }}>No filterable fields yet — wait for the widget to load.</div>
      ) : (
        <>
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            {rowsState.map((f, i) => (
              <window.FilterRow key={i} f={f} source={null} cols={cols} fetchValues={fetchValues}
                onChange={(patch) => setRowsState(rowsState.map((x, j) => j === i ? { ...x, ...patch } : x))}
                onRemove={() => setRowsState(rowsState.filter((_, j) => j !== i))} />
            ))}
          </div>
          <button onClick={() => setRowsState([...rowsState, { column: '', op: '=', value: '', value2: '' }])}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)', padding: 0 }}>+ Add filter</button>
        </>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10 }}>
        <window.Btn size="xs" variant="accent" onClick={() => apply(window.coerceFilters(rowsState, colCat))}>Apply</window.Btn>
        <window.Btn size="xs" variant="ghost" disabled={!rowsState.length && !(value || []).length} onClick={() => apply([])}>Clear</window.Btn>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <window.Btn size="xs" variant="outline" title="Everyone starts with these filters on this widget"
            onClick={() => { const wire = window.coerceFilters(rowsState, colCat); onApply(wire); onSaveDefault(wire); onClose(); }}>
            Save as default
          </window.Btn>
        )}
      </div>
    </div>
  );
};

const WidgetCard = ({ dashId, widget, canEdit, filters, embedded = false, widgetFilters, onWidgetFilters, crossFilterCols, onCrossFilter, onRemove, onResize, onResizeH, span = 2, rows = 1, refreshNonce = 0, onChanged, onDragStart, onDragOver, onDrop, dragging }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [editSpec, setEditSpec] = useState(null);   // non-null = "Edit chart" drawer open
  const [editBusy, setEditBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);   // funnel popover
  const [maximized, setMaximized] = useState(false);     // ephemeral fullscreen overlay
  const menuRef = useRef(null);
  const maxRef = useRef(null);
  // Esc closes the overlay — the stack-aware hook lets a drill drawer or the
  // export drawer opened on top close first.
  window.useFocusTrap(maximized, maxRef);
  window.useEscapeKey(maximized, () => setMaximized(false));
  // Drop filters scoped to other widgets, then strip the targeting field — the
  // backend filter model doesn't carry widget_ids. The viewer's per-widget
  // filters AND-concat after them (same column in both = both applied).
  const wf = widgetFilters || [];
  const applied = [
    ...(filters || [])
      .filter((f) => !f.widget_ids || f.widget_ids.includes(widget.id))
      .map(({ widget_ids, ...rest }) => rest),
    ...wf.map((f) => ({ column: f.column, op: f.op, value: f.value })),
  ];
  const filtersKey = JSON.stringify(applied);

  // Keep the last-good data on a failed refresh — surface the error as a badge
  // rather than blanking the chart. Re-runs whenever the active filters change.
  const load = useCallback(async (refresh) => {
    setError('');
    try {
      const res = await limitedRun(() => window.AtlasApi.analytics.runWidget(dashId, widget.id, refresh, applied));
      setData(res);
    } catch (e) { setError(_err(e)); }
  }, [dashId, widget.id, filtersKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Grids too big for one run (see `has_more`) fetch the next page and APPEND.
  // The server pages the frozen spec SQL, so ordering is stable across pages —
  // rows only ever grow, and `has_more` from the newest page decides whether
  // there is another. Reset happens naturally: a filter change re-runs `load`,
  // which replaces `data` with page 0.
  const [pageBusy, setPageBusy] = useState(false);
  const loadMore = useCallback(async () => {
    if (pageBusy || !data || !data.has_more) return;
    setPageBusy(true);
    try {
      const next = await limitedRun(() => window.AtlasApi.analytics.runWidget(
        dashId, widget.id, false, applied, (data.rows || []).length));
      setData((prev) => (prev ? {
        ...next,
        rows: [...(prev.rows || []), ...(next.rows || [])],
        row_count: (prev.rows || []).length + (next.rows || []).length,
      } : next));
    } catch (e) { window.pushToast({ type: 'error', title: 'Could not load more rows', body: _err(e) }); }
    finally { setPageBusy(false); }
  }, [dashId, widget.id, filtersKey, data, pageBusy]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(false); }, [load]);
  // "Refresh all": the canvas bumps refreshNonce; every card re-runs bypassing
  // the cache. Starts at 0 so the initial mount doesn't double-fetch.
  useEffect(() => { if (refreshNonce) load(true); }, [refreshNonce]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const spec = widget.spec || {};
  const bodyH = WIDGET_HEIGHT[rows] || WIDGET_HEIGHT[1];
  // Fullscreen body height: the 92vh panel minus its header + body padding.
  const maxBodyH = maximized ? Math.max(300, Math.round(window.innerHeight * 0.92) - 92) : 0;

  const duplicate = async () => {
    try {
      await window.AtlasApi.analytics.createWidget(dashId, {
        title: `${widget.title} (copy)`, spec,
        layout: widget.layout || {}, source_request: widget.source_request || null,
      });
      window.pushToast({ type: 'success', title: 'Widget duplicated' });
      onChanged && onChanged();
    } catch (e) { window.pushToast({ type: 'error', title: 'Duplicate failed', body: _err(e) }); }
  };

  // Re-ask the AI the widget's original question (refining the current spec) —
  // the fix for the "schema changed" pill. Spends an LLM call, so confirm first.
  const regenerate = async () => {
    if (regenBusy || !widget.source_request) return;
    const ok = await window.showConfirm({
      title: 'Regenerate with AI?',
      body: `Re-ask the AI: “${widget.source_request}” — replaces the widget's chart and query.`,
      confirmLabel: 'Regenerate',
    });
    if (!ok) return;
    setRegenBusy(true);
    try {
      const res = await window.AtlasApi.analytics.generate({ request: widget.source_request, base_spec: spec });
      await window.AtlasApi.analytics.updateWidget(dashId, widget.id, { spec: res.spec });
      window.pushToast({ type: 'success', title: 'Widget regenerated' });
      onChanged && onChanged();
      load(true);
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Regenerate failed', body: _err(e) });
    } finally { setRegenBusy(false); }
  };

  const saveEditedSpec = async () => {
    if (editBusy || !editSpec) return;
    setEditBusy(true);
    try {
      await window.AtlasApi.analytics.updateWidget(dashId, widget.id, { spec: editSpec });
      setEditSpec(null);
      onChanged && onChanged();
    } catch (e) { window.pushToast({ type: 'error', title: 'Update failed', body: _err(e) }); }
    finally { setEditBusy(false); }
  };

  // Persist the current widget filters as the author default — merged over the
  // existing layout so w/h survive (and a later resize spreads this key back).
  const saveDefaultFilters = async (wire) => {
    try {
      await window.AtlasApi.analytics.updateWidget(dashId, widget.id, { layout: { ...(widget.layout || {}), default_filters: wire } });
      window.pushToast({ type: 'success', title: wire.length ? 'Widget default filters saved' : 'Widget default filters cleared' });
      onChanged && onChanged();
    } catch (e) { window.pushToast({ type: 'error', title: "Couldn't save defaults", body: _err(e) }); }
  };
  const wfCols = useMemo(() => widgetFilterCols(spec, data), [spec, data]);
  return (
    <div
      draggable={canEdit}
      onDragStart={canEdit ? onDragStart : undefined}
      onDragOver={canEdit ? onDragOver : undefined}
      onDrop={canEdit ? onDrop : undefined}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        display: 'flex', flexDirection: 'column', opacity: dragging ? 0.5 : 1,
        gridColumn: 'span ' + span, gridRow: 'span ' + rows, minWidth: 0,
      }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: canEdit ? 'grab' : 'default' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widget.title}</span>
        {data && data.ran_at && (
          <span style={{ fontSize: 10, color: 'var(--text-4)' }} title={`Last refreshed ${new Date(data.ran_at).toLocaleString()}`}>
            {window.relativeTime(data.ran_at)}{data.cached ? ' · cached' : ''}
          </span>
        )}
        {data && data.schema_changed && (
          <span title="The underlying tables changed since this widget was built — the numbers may be off. Regenerate it to be safe."
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <window.Pill hue="amber" sz="sm">schema changed</window.Pill>
            {canEdit && widget.source_request && (
              <button onClick={regenerate} disabled={regenBusy} onMouseDown={(e) => e.stopPropagation()} draggable={false}
                title="Re-ask the AI the widget's original question"
                style={{ border: 'none', background: 'transparent', cursor: regenBusy ? 'default' : 'pointer', color: 'var(--accent-text)', fontSize: 10.5, fontWeight: 600, padding: '1px 3px' }}>
                {regenBusy ? 'Regenerating…' : 'Regenerate'}
              </button>
            )}
          </span>
        )}
        {error && data && (
          <span title={error}><window.Pill hue="rose" sz="sm">refresh failed</window.Pill></span>
        )}
        {canEdit && onResize && (
          <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 5, overflow: 'hidden' }}
            onMouseDown={(e) => e.stopPropagation()} draggable={false} title="Resize widget width">
            <button disabled={span <= 1} onClick={(e) => { e.stopPropagation(); onResize(-1); }}
              style={{ border: 'none', background: 'transparent', cursor: span <= 1 ? 'default' : 'pointer', color: 'var(--text-3)', padding: '0 6px', fontSize: 14, lineHeight: 1.6, opacity: span <= 1 ? 0.35 : 1 }}>−</button>
            <span style={{ fontSize: 9.5, color: 'var(--text-4)', padding: '0 1px', minWidth: 18, textAlign: 'center' }} className="num">{span}/4</span>
            <button disabled={span >= 4} onClick={(e) => { e.stopPropagation(); onResize(1); }}
              style={{ border: 'none', background: 'transparent', cursor: span >= 4 ? 'default' : 'pointer', color: 'var(--text-3)', padding: '0 6px', fontSize: 14, lineHeight: 1.6, opacity: span >= 4 ? 0.35 : 1 }}>+</button>
          </div>
        )}
        {canEdit && onResizeH && (
          <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 5, overflow: 'hidden' }}
            onMouseDown={(e) => e.stopPropagation()} draggable={false} title="Resize widget height">
            <button disabled={rows <= 1} onClick={(e) => { e.stopPropagation(); onResizeH(-1); }}
              style={{ border: 'none', background: 'transparent', cursor: rows <= 1 ? 'default' : 'pointer', color: 'var(--text-3)', padding: '0 6px', fontSize: 14, lineHeight: 1.6, opacity: rows <= 1 ? 0.35 : 1 }}>−</button>
            <span style={{ fontSize: 9.5, color: 'var(--text-4)', padding: '0 1px', minWidth: 22, textAlign: 'center' }} className="num">↕{rows}/3</span>
            <button disabled={rows >= 3} onClick={(e) => { e.stopPropagation(); onResizeH(1); }}
              style={{ border: 'none', background: 'transparent', cursor: rows >= 3 ? 'default' : 'pointer', color: 'var(--text-3)', padding: '0 6px', fontSize: 14, lineHeight: 1.6, opacity: rows >= 3 ? 0.35 : 1 }}>+</button>
          </div>
        )}
        <button onClick={() => setMaximized(true)} title="Maximize"
          onMouseDown={(e) => e.stopPropagation()} draggable={false}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
          <window.Icon.Maximize size={13} />
        </button>
        {!embedded && (
          <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()} draggable={false}>
            <button onClick={() => setFilterOpen((v) => !v)} title="Filter this widget"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: wf.length ? 'var(--accent-text)' : 'var(--text-3)', padding: 2, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <window.Icon.Filter size={13} />
              {wf.length > 0 && <span className="num" style={{ fontSize: 9.5, fontWeight: 700 }}>{wf.length}</span>}
            </button>
            {filterOpen && (
              <WidgetFilterPopover spec={spec} cols={wfCols} value={wf}
                defaults={(widget.layout && widget.layout.default_filters) || []}
                canEdit={canEdit} onApply={onWidgetFilters} onSaveDefault={saveDefaultFilters}
                onClose={() => setFilterOpen(false)} />
            )}
          </div>
        )}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button onClick={() => setMenuOpen((v) => !v)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
            <window.Icon.More size={14} />
          </button>
          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: 24, zIndex: 30, minWidth: 150, background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 7, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
              <MenuItem icon={<window.Icon.Maximize size={12} />} label="Maximize" onClick={() => { setMenuOpen(false); setMaximized(true); }} />
              <MenuItem icon={<window.Icon.Refresh size={12} />} label="Refresh" onClick={() => { setMenuOpen(false); load(true); }} />
              <MenuItem icon={<window.Icon.External size={12} />} label="Export CSV"
                onClick={() => { setMenuOpen(false); setCsvOpen(true); }} />
              {canEdit && <MenuItem icon={<window.Icon.Edit size={12} />} label="Rename" onClick={() => { setMenuOpen(false); setRenameOpen(true); }} />}
              {canEdit && <MenuItem icon={<window.Icon.ChartBar size={12} />} label="Edit chart" onClick={() => { setMenuOpen(false); setEditSpec({ ...spec }); }} />}
              {canEdit && <MenuItem icon={<window.Icon.Copy size={12} />} label="Duplicate" onClick={() => { setMenuOpen(false); duplicate(); }} />}
              {canEdit && widget.source_request && (
                <MenuItem icon={<window.Icon.Sparkle size={12} />} label={regenBusy ? 'Regenerating…' : 'Regenerate (AI)'}
                  onClick={() => { setMenuOpen(false); regenerate(); }} />
              )}
              {canEdit && <MenuItem icon={<window.Icon.Trash size={12} />} label="Remove" danger onClick={() => { setMenuOpen(false); onRemove(); }} />}
            </div>
          )}
        </div>
      </header>
      {wf.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', padding: '7px 12px 0' }}>
          {wf.map((f, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
              {wfChipText(f)}
              {!embedded && (
                <button onClick={() => onWidgetFilters(wf.filter((_, j) => j !== i))} title="Remove this filter"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
              )}
            </span>
          ))}
          {!embedded && (
            <button onClick={() => onWidgetFilters([])}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 10.5, color: 'var(--text-4)', padding: '0 2px' }}>clear</button>
          )}
        </div>
      )}
      <div style={{ flex: 1, padding: 10, minHeight: rows > 1 ? bodyH : 200 }}>
        {data !== null ? (
          <window.WidgetRenderer spec={spec} data={data} height={bodyH}
            onLoadMore={data.has_more ? loadMore : null} loadingMore={pageBusy}
            onSelect={(sel) => setDrill({ selection: sel })} />
        ) : error ? (
          <div style={{ fontSize: 11.5, color: 'var(--rose-fg)', padding: 10 }}>{error}</div>
        ) : (
          <window.Skeleton w="100%" h={rows > 1 ? bodyH - 40 : 200} style={{ borderRadius: 6 }} />
        )}
      </div>
      {maximized && (
        // Fullscreen overlay — ephemeral view state, same already-loaded
        // data/spec at panel size. z 60 (the modal tier): the drill and export
        // drawers stack above at 70, menus/dialogs (300/400) stay on top.
        <div onClick={() => setMaximized(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'oklch(0.2 0.01 270 / 0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div ref={maxRef} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)', width: '95vw', height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widget.title}</span>
              {applied.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'flex-end', maxWidth: '45%' }}>
                  {applied.map((f, i) => (
                    <span key={i} style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)', whiteSpace: 'nowrap' }}>{wfChipText(f)}</span>
                  ))}
                </div>
              )}
              <window.Btn size="sm" variant="outline" icon={<window.Icon.Refresh size={12} />} onClick={() => load(true)}>Refresh</window.Btn>
              <window.Btn size="sm" variant="outline" icon={<window.Icon.External size={12} />} onClick={() => setCsvOpen(true)}>Export CSV</window.Btn>
              <button onClick={() => setMaximized(false)} aria-label="Close" title="Close (Esc)"
                style={{ width: 26, height: 26, borderRadius: 5, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', fontSize: 18, lineHeight: 1 }}>×</button>
            </header>
            <div style={{ flex: 1, minHeight: 0, padding: 14, overflow: 'auto' }}>
              {data !== null ? (
                <window.WidgetRenderer spec={spec} data={data} height={maxBodyH}
                  onLoadMore={data.has_more ? loadMore : null} loadingMore={pageBusy}
                  onSelect={(sel) => setDrill({ selection: sel })} />
              ) : (
                <window.Skeleton w="100%" h={maxBodyH} style={{ borderRadius: 6 }} />
              )}
            </div>
          </div>
        </div>
      )}
      {renameOpen && (
        <EditDialog title="Rename widget" label="Title" initial={widget.title}
          onSubmit={async (v) => {
            if (v) {
              try { await window.AtlasApi.analytics.updateWidget(dashId, widget.id, { title: v }); onChanged && onChanged(); }
              catch (e) { window.pushToast({ type: 'error', title: 'Rename failed', body: _err(e) }); }
            }
            setRenameOpen(false);
          }}
          onClose={() => setRenameOpen(false)} />
      )}
      {editSpec && (
        <window.Drawer open onClose={() => setEditSpec(null)} title="Edit chart" subtitle={widget.title} width={560}
          footer={<>
            <window.Btn size="sm" variant="ghost" onClick={() => setEditSpec(null)} disabled={editBusy}>Cancel</window.Btn>
            <window.Btn size="sm" variant="accent" onClick={saveEditedSpec} disabled={editBusy}>{editBusy ? 'Saving…' : 'Save'}</window.Btn>
          </>}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 14 }}>
            {data
              ? <window.WidgetRenderer spec={editSpec} data={data} height={editSpec.chart_type === 'table' ? undefined : 220} />
              : <window.Skeleton w="100%" h={180} style={{ borderRadius: 6 }} />}
          </div>
          <SpecEditor spec={editSpec} setSpec={setEditSpec} columns={(data && data.columns) || []} />
        </window.Drawer>
      )}
      {csvOpen && (
        <window.ExportDialog title={widget.title || spec.title || 'Widget'}
          columns={exportColumns(spec, data && data.columns)} zIndex={maximized ? 70 : undefined}
          onClose={() => setCsvOpen(false)}
          onExport={(cols) => window.AtlasApi.analytics.exportWidgetCsv(dashId, widget.id, { filters: applied, columns: cols || undefined })} />
      )}
      {drill && <DrillInspector spec={spec} data={data} selection={drill.selection}
        dashId={dashId} widgetId={widget.id} filters={applied} zIndex={maximized ? 70 : undefined}
        crossFilterCols={crossFilterCols} onCrossFilter={onCrossFilter} onClose={() => setDrill(null)} />}
    </div>
  );
};

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 11px', border: 'none',
    background: 'transparent', cursor: 'pointer', fontSize: 12, textAlign: 'left',
    color: danger ? 'var(--rose-fg)' : 'var(--text-2)',
  }}
    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
    {icon}{label}
  </button>
);

// ── Dashboard filters: shared building blocks ───────────────────────────────
const DASH_FILTER_INPUT = { fontSize: 12, padding: '4px 7px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)' };

const FILTER_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'select', label: 'Select (list)' },
  { value: 'date', label: 'Date range' },
  { value: 'asof', label: 'As-of day (column as_of)' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'scope', label: 'Scope (me / team)' },
];
const OPS_BY_TYPE = {
  text: [{ value: 'contains', label: 'contains' }, { value: '=', label: 'equals' }, { value: '!=', label: 'not equals' }, { value: 'starts_with', label: 'starts with' }],
  number: [{ value: '>=', label: '≥ (min)' }, { value: '<=', label: '≤ (max)' }, { value: '=', label: 'equals' }, { value: '>', label: '>' }, { value: '<', label: '<' }],
};
const DATE_PRESETS = [
  { key: 'ytd', label: 'Year to date' }, { key: 'qtd', label: 'Quarter to date' }, { key: 'mtd', label: 'Month to date' },
  { key: 'w4', label: 'Last 4 weeks' }, { key: 'w12', label: 'Last 12 weeks' },
  { key: 'd30', label: 'Last 30 days' }, { key: 'd90', label: 'Last 90 days' }, { key: 'prevyear', label: 'Previous year' },
  { key: 'ytd_fwd', label: 'This year + 12 months' },
];
const _isoLocal = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
const resolvePreset = (key) => {
  const now = new Date(); const today = _isoLocal(now); const y = now.getFullYear(), m = now.getMonth();
  const mk = (yr, mo, da) => _isoLocal(new Date(yr, mo, da));
  const back = (days) => { const d = new Date(now); d.setDate(d.getDate() - days); return { from: _isoLocal(d), to: today }; };
  switch (key) {
    case 'ytd': return { from: mk(y, 0, 1), to: today };
    // Forward-looking: Jan 1 through a year out. For a date that hasn't
    // happened yet (an estimated close), a window ending today is empty ahead.
    case 'ytd_fwd': return { from: mk(y, 0, 1), to: (() => { const d = new Date(now); d.setDate(d.getDate() + 365); return _isoLocal(d); })() };
    case 'qtd': return { from: mk(y, Math.floor(m / 3) * 3, 1), to: today };
    case 'mtd': return { from: mk(y, m, 1), to: today };
    case 'w4': return back(28);
    case 'w12': return back(84);
    case 'd30': return back(30);
    case 'd90': return back(90);
    case 'prevyear': return { from: mk(y - 1, 0, 1), to: mk(y - 1, 11, 31) };
    default: return { from: '', to: '' };
  }
};

// Compact segmented toggle (boolean / scope filters).
const Segmented = ({ options, value, onChange }) => (
  <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
    {options.map((o, i) => {
      const on = String(value) === String(o.value) || (value == null && o.value == null);
      return <button key={i} onClick={() => onChange(o.value)} style={{ border: 'none', cursor: 'pointer', fontSize: 11,
        padding: '4px 9px', background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--accent-text)' : 'var(--text-3)' }}>{o.label}</button>;
    })}
  </div>
);

// Searchable multi/single-select popover for select-type filters + widget targeting.
const MultiSelect = ({ value, options, placeholder, onChange, single }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const sel = single ? (value != null && value !== '' ? [value] : []) : (Array.isArray(value) ? value : []);
  const selStr = sel.map(String);
  const labelFor = (v) => { const o = options.find((x) => String(x.value) === String(v)); return o ? o.label : String(v); };
  const toggle = (v) => {
    if (single) { onChange(v); setOpen(false); return; }
    const set = new Set(selStr);
    if (set.has(String(v))) set.delete(String(v)); else set.add(String(v));
    onChange(options.filter((o) => set.has(String(o.value))).map((o) => o.value));
  };
  const shown = q ? options.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase())) : options;
  const label = sel.length === 0 ? (placeholder || 'Any') : single ? labelFor(sel[0]) : (sel.length === 1 ? labelFor(sel[0]) : `${sel.length} selected`);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ ...DASH_FILTER_INPUT, display: 'inline-flex', alignItems: 'center', gap: 6,
        cursor: 'pointer', minWidth: 130, maxWidth: 220, justifyContent: 'space-between' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: sel.length ? 'var(--text)' : 'var(--text-4)' }}>{label}</span>
        <span style={{ color: 'var(--text-4)', fontSize: 9 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60, width: 240, maxHeight: 300, overflow: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 6 }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...DASH_FILTER_INPUT, width: '100%', marginBottom: 4, boxSizing: 'border-box' }} />
          {!single && sel.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => onChange([])} style={{ border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }}>Clear</button>
            </div>
          )}
          {shown.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '6px 8px' }}>No options</div>}
          {shown.map((o) => {
            const on = selStr.includes(String(o.value));
            return (
              <button key={String(o.value)} onClick={() => toggle(o.value)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                border: 'none', background: on ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', padding: '5px 8px', borderRadius: 5, fontSize: 12, color: 'var(--text-2)', textAlign: 'left' }}>
                {!single && <span style={{ width: 13, height: 13, borderRadius: 3, border: '1px solid var(--border-strong)', background: on ? 'var(--accent)' : 'transparent',
                  flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9 }}>{on ? '✓' : ''}</span>}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                {o.count != null && <span style={{ fontSize: 10, color: 'var(--text-4)' }} className="num">{o.count}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// One viewer-facing filter control, dispatched by filter type.
const FilterControl = ({ def, value, onChange }) => {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (def.type !== 'select') return;
    if (Array.isArray(def.options) && def.options.length) { setOpts(def.options.map((o) => ({ value: o.value, label: o.label != null ? o.label : String(o.value) }))); return; }
    const src = def.source, col = def.value_column || def.column;
    if (!src || !col) { setOpts([]); return; }
    let alive = true;
    window.AtlasApi.analytics.values(src, col)
      .then((rows) => { if (alive) setOpts((Array.isArray(rows) ? rows : []).map((r) => ({ value: r.value, label: r.value == null ? '—' : String(r.value), count: r.count }))); })
      .catch(() => { if (alive) setOpts([]); });
    return () => { alive = false; };
  }, [def.type, def.source, def.value_column, def.column, JSON.stringify(def.options || null)]);

  if (def.type === 'select') return <MultiSelect single={!def.multi} value={value} options={opts || []} placeholder={opts ? 'Any' : 'Loading…'} onChange={onChange} />;
  if (def.type === 'date') {
    const v = value || {};
    const setPreset = (key) => onChange(key ? { ...resolvePreset(key), preset: key } : {});
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <window.Picker value={v.preset || ''} onChange={setPreset} size="sm" emptyLabel="Custom…" placeholder="Date range"
          options={DATE_PRESETS.map((p) => ({ value: p.key, label: p.label }))} />
        <input type="date" value={v.from || ''} style={DASH_FILTER_INPUT} onChange={(e) => onChange({ ...v, from: e.target.value, preset: undefined })} />
        <span style={{ color: 'var(--text-4)', fontSize: 11 }}>→</span>
        <input type="date" value={v.to || ''} style={DASH_FILTER_INPUT} onChange={(e) => onChange({ ...v, to: e.target.value, preset: undefined })} />
      </div>
    );
  }
  // A single day that rebinds time-window widgets' "today" — empty means today.
  if (def.type === 'asof') return <input type="date" value={value || ''} title="Empty = today" style={DASH_FILTER_INPUT} onChange={(e) => onChange(e.target.value || undefined)} />;
  if (def.type === 'number') {
    const v = value || {};
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <input type="number" value={v.min ?? ''} placeholder="min" style={{ ...DASH_FILTER_INPUT, width: 64 }} onChange={(e) => onChange({ ...v, min: e.target.value })} />
        <span style={{ color: 'var(--text-4)', fontSize: 11 }}>–</span>
        <input type="number" value={v.max ?? ''} placeholder="max" style={{ ...DASH_FILTER_INPUT, width: 64 }} onChange={(e) => onChange({ ...v, max: e.target.value })} />
      </div>
    );
  }
  if (def.type === 'boolean') return <Segmented value={value} onChange={onChange} options={[{ value: undefined, label: 'Any' }, { value: true, label: 'Yes' }, { value: false, label: 'No' }]} />;
  if (def.type === 'scope') return <Segmented value={value || 'all'} onChange={onChange} options={[{ value: 'all', label: 'Everyone' }, { value: 'me', label: 'Me' }, { value: 'team', label: 'My team' }]} />;
  return <input value={value || ''} placeholder="contains…" style={{ ...DASH_FILTER_INPUT, width: 140 }} onChange={(e) => onChange(e.target.value)} />;
};

// ── Dashboard filter definitions editor (admin) ─────────────────────────────
let _filterIdSeq = 0;
const FiltersDialog = ({ dash, candidates, widgets, onClose, onSaved }) => {
  const [defs, setDefs] = useState(() => (dash.filters || []).map((d) => ({ ...d })));
  const [busy, setBusy] = useState(false);
  const colOpts = candidates.map((c) => ({ value: c, label: window.humanizeCol ? window.humanizeCol(c) + ' · ' + c : c }));
  const addDef = () => setDefs([...defs, { id: `f${Date.now().toString(36)}${++_filterIdSeq}`, label: '', column: candidates[0] || '', type: 'text' }]);
  const setDef = (i, patch) => setDefs(defs.map((d, j) => j === i ? { ...d, ...patch } : d));

  // Union of all widgets' declared `filterable` defs, keyed by column — picking
  // one of these columns prefills type/source/value_column from the def, and
  // their sources feed the suggestion chips below the free-text inputs.
  const fcByKey = useMemo(() => {
    const m = {};
    for (const w of (widgets || [])) {
      for (const fc of ((w.spec && w.spec.filterable) || [])) {
        if (fc && fc.key && !m[fc.key]) m[fc.key] = fc;
      }
    }
    return m;
  }, [widgets]);
  const sourceSuggestions = useMemo(() => [...new Set(Object.values(fcByKey).map((fc) => fc.source).filter(Boolean))].sort(), [fcByKey]);
  const pickColumn = (i, d, c) => {
    const fc = fcByKey[c];
    const patch = { column: c };
    if (fc) {
      if (fc.type && FILTER_TYPES.some((t) => t.value === fc.type) && fc.type !== d.type) { patch.type = fc.type; patch.op = undefined; }
      const t = patch.type || d.type;
      if (t === 'select') { if (fc.source) patch.source = fc.source; if (fc.value_column) patch.value_column = fc.value_column; }
    }
    setDef(i, patch);
  };
  const pickType = (i, d, t) => {
    const patch = { type: t, op: undefined };
    const fc = fcByKey[d.column];
    if (t === 'select' && fc) {
      if (fc.source && !d.source) patch.source = fc.source;
      if (fc.value_column && !d.value_column) patch.value_column = fc.value_column;
    }
    setDef(i, patch);
  };

  const save = async () => {
    // Validate before shipping: every row needs a label + column; a select
    // without static options needs a source for its value list.
    for (const d of defs) {
      const name = (d.label || '').trim() || (d.column ? _hc(d.column) : 'A filter');
      if (!(d.label || '').trim()) { window.pushToast({ type: 'error', title: 'Filter needs a label', body: 'Give every filter a label, or remove the row.' }); return; }
      if (!d.column) { window.pushToast({ type: 'error', title: `“${name}” needs a column` }); return; }
      if (d.type === 'select' && !(Array.isArray(d.options) && d.options.length) && !(d.source || '').trim()) {
        window.pushToast({ type: 'error', title: `“${name}” needs an options source`, body: 'Select filters load their values from a source table/view.' });
        return;
      }
    }
    setBusy(true);
    try {
      const clean = defs.map((d) => {
        const out = { id: d.id, label: d.label.trim(), column: d.column, type: d.type };
        if (d.op) out.op = d.op;
        if (d.type === 'select') { out.multi = !!d.multi; if (d.source) out.source = d.source.trim(); if (d.value_column) out.value_column = d.value_column.trim(); }
        if (d.type === 'date' && d.default && d.default.preset) out.default = { preset: d.default.preset };
        if (Array.isArray(d.widget_ids) && d.widget_ids.length) out.widget_ids = d.widget_ids;
        if (Array.isArray(d.options) && d.options.length) out.options = d.options;
        return out;
      });
      await window.AtlasApi.analytics.updateDashboard(dash.id, { filters: clean });
      window.pushToast({ type: 'success', title: 'Filters updated' });
      onSaved();
    } catch (e) { window.pushToast({ type: 'error', title: 'Update failed', body: _err(e) }); }
    finally { setBusy(false); }
  };

  const widgetOpts = (widgets || []).map((w) => ({ value: w.id, label: w.title }));
  return (
    <window.Drawer open onClose={onClose} title="Dashboard filters" width={520}
      footer={<>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save filters'}</window.Btn>
      </>}>
      {candidates.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
          Add some widgets first — filters target columns those widgets use.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {defs.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <window.TextInput value={d.label} placeholder="Label (e.g. Dealmaker)" onChange={(e) => setDef(i, { label: e.target.value })} style={{ flex: 1 }} />
              <button onClick={() => setDefs(defs.filter((_, j) => j !== i))} title="Remove filter" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', fontSize: 15 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <window.Combobox value={d.column} searchable onChange={(e) => pickColumn(i, d, e.target.value)} options={colOpts} style={{ flex: 1 }} />
              <window.Combobox value={d.type} onChange={(e) => pickType(i, d, e.target.value)} options={FILTER_TYPES} style={{ width: 150 }} />
            </div>
            {d.type === 'select' && (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!d.multi} onChange={(e) => setDef(i, { multi: e.target.checked })} /> multiple
                  </label>
                  <window.TextInput value={d.source || ''} placeholder="options source (e.g. team_members)" onChange={(e) => setDef(i, { source: e.target.value })} style={{ flex: 1, minWidth: 150 }} />
                  <window.TextInput value={d.value_column || ''} placeholder="value column" onChange={(e) => setDef(i, { value_column: e.target.value })} style={{ width: 120 }} />
                </div>
                {(() => {
                  // Suggestions from the widgets' filterable defs — a chip fills
                  // both fields; free-typed custom values still work.
                  const fc = fcByKey[d.column];
                  const suggs = (fc && fc.source) ? [fc] : sourceSuggestions.map((s) => ({ source: s }));
                  const shown = suggs.filter((s) => s.source !== d.source || (s.value_column || '') !== (d.value_column || ''));
                  if (!shown.length) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                      <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>Suggested:</span>
                      {shown.slice(0, 6).map((s, j) => (
                        <button key={j} onClick={() => setDef(i, { source: s.source, value_column: s.value_column || d.value_column })}
                          style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer' }}>
                          {s.source}{s.value_column ? ` · ${s.value_column}` : ''}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
            {(d.type === 'text' || d.type === 'number') && (
              <window.Combobox value={d.op || (d.type === 'number' ? '>=' : 'contains')} onChange={(e) => setDef(i, { op: e.target.value })} options={OPS_BY_TYPE[d.type]} style={{ width: 170 }} />
            )}
            {d.type === 'date' && (
              <window.Combobox value={(d.default && d.default.preset) || ''} onChange={(e) => setDef(i, { default: e.target.value ? { preset: e.target.value } : undefined })}
                options={[{ value: '', label: 'No default range' }, ...DATE_PRESETS.map((p) => ({ value: p.key, label: 'Default: ' + p.label }))]} style={{ width: 220 }} />
            )}
            {widgetOpts.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Applies to</span>
                <MultiSelect value={d.widget_ids || []} options={widgetOpts} placeholder="all widgets" onChange={(vals) => setDef(i, { widget_ids: vals.length ? vals : undefined })} />
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={addDef} disabled={!candidates.length} style={{ marginTop: 8, border: 'none', background: 'transparent', cursor: candidates.length ? 'pointer' : 'not-allowed', fontSize: 11.5, color: 'var(--accent-text)', opacity: candidates.length ? 1 : 0.5 }}>+ Add filter</button>
    </window.Drawer>
  );
};

// ── Dashboard canvas ─────────────────────────────────────────────────────────
// Shareable dashboard links: an OPEN dashboard writes
//   #/analytics/dashboards/<id>[?f=<urlencoded JSON of the viewer's filter
//   selections>]
// via replaceState (no history spam). The app router treats the extra
// segments/query as a refinement of #/analytics (router.jsx writeHash), and
// AnalyticsView's initializer re-opens the dashboard from the hash on mount.
const dashHashBase = (dashId) => `/analytics/dashboards/${encodeURIComponent(dashId)}`;
// The `?f=` payload comes in two shapes: legacy = a plain {defId: selection}
// object (dashboard selections only); v2 = {v: 2, d: {defId: selection},
// w: {widgetId: [{column, op, value}, …]}} carrying per-widget filters too.
// Both decode — legacy links keep working, treated as dashboard-only.
const parseDashHashFilters = (dashId) => {
  try {
    const h = window.location.hash || '';
    const base = '#' + dashHashBase(dashId);
    if (!h.startsWith(base + '?')) return null;
    const m = /(?:^|[?&])f=([^&]*)/.exec(h.slice(base.length));
    if (!m || !m[1]) return null;
    const parsed = JSON.parse(decodeURIComponent(m[1]));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (parsed.v === 2) {
      const ok = (x) => (x && typeof x === 'object' && !Array.isArray(x)) ? x : null;
      return { d: ok(parsed.d), w: ok(parsed.w) };
    }
    return { d: parsed, w: null };
  } catch (_) { return null; }
};

// `embedded` (Home's pinned-dashboard section): view-only surface — forces
// canEdit off, hides the canvas header (the host renders its own), and skips
// the URL sync (Home's hash isn't this dashboard's route).
const DashboardCanvas = ({ dashId, onBack, onAddWidget, onShare, embedded = false }) => {
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [editFilters, setEditFilters] = useState(false);
  const [editMeta, setEditMeta] = useState(false);    // name/description dialog
  const [refreshNonce, setRefreshNonce] = useState(0); // bump → every widget re-runs, bypassing cache
  const dragIdx = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [members, setMembers] = useState([]);
  useEffect(() => { window.AtlasApi.listTeamMembers().then((m) => setMembers(Array.isArray(m) ? m : [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setError('');
    window.AtlasApi.analytics.getDashboard(dashId).then(setDash).catch((e) => setError(_err(e)));
  }, [dashId]);
  useEffect(() => { load(); }, [load]);

  const canEdit = !embedded && dash && dash.can_edit;
  const filterDefs = (dash && dash.filters) || [];
  const lsKey = `atlas.dashFilters.${dashId}`;
  const wfLsKey = `atlas.widgetFilters.${dashId}`;
  // Per-widget viewer filters: {widgetId: [{column, op, value}, …]} — wire
  // shape, AND-merged with the dashboard-level filters inside each WidgetCard.
  const [widgetFilters, setWidgetFilters] = useState({});

  // Filter state from a shared URL — captured once during the FIRST render
  // (render phase, so it wins the race against app.jsx's hash-normalizing
  // effects), applied over localStorage in the seed effect below: a shared
  // link should show exactly what the sender saw.
  const urlFilters = useRef(embedded ? null : parseDashHashFilters(dashId));

  // Resolve the "scope" control (Me / My team) against team membership.
  const meId = (() => { try { return localStorage.getItem('atlas.activeUserId'); } catch (_) { return null; } })();
  const me = members.find((m) => String(m.id) === String(meId));
  const scopeMe = me ? me.full_name : null;
  const scopeTeam = useMemo(() => {
    if (!me) return [];
    const dir = me.director_id;
    const team = members.filter((m) => m.id === me.id || m.director_id === me.id || (dir && (m.director_id === dir || m.id === dir)));
    return [...new Set(team.map((m) => m.full_name).filter(Boolean))];
  }, [JSON.stringify(members), meId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Seed viewer values from each filter's default, then restore any saved
  // selection (localStorage, per dashboard). Keyed on the filter DEFINITIONS so
  // a plain dashboard reload doesn't clobber the viewer's current picks.
  useEffect(() => {
    if (!dash) return;
    const init = {};
    for (const f of filterDefs) {
      if (f.default == null) continue;
      if (f.type === 'date' && f.default.preset) init[f.id] = { ...resolvePreset(f.default.preset), preset: f.default.preset };
      else init[f.id] = f.default;
    }
    try { const saved = JSON.parse(localStorage.getItem(lsKey) || 'null'); if (saved && typeof saved === 'object') Object.assign(init, saved); } catch (_) { /* ignore */ }
    // URL beats localStorage (one-shot — later filter edits are the viewer's).
    if (urlFilters.current && urlFilters.current.d) { Object.assign(init, urlFilters.current.d); urlFilters.current = { ...urlFilters.current, d: null }; }
    setFilterValues(init);
  }, [dashId, JSON.stringify(filterDefs)]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Seed per-widget filters: author defaults (layout.default_filters) →
  // saved viewer selections (localStorage) → shared URL (wins). Keyed on the
  // widget ROSTER so a plain canvas reload doesn't clobber the viewer's picks;
  // a saved entry (even []) beats the default — clearing sticks.
  useEffect(() => {
    if (!dash) return;
    const init = {};
    for (const w of dash.widgets) {
      const df = w.layout && Array.isArray(w.layout.default_filters) ? w.layout.default_filters : null;
      if (df && df.length) init[w.id] = df;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(wfLsKey) || 'null');
      if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
        for (const [k, v] of Object.entries(saved)) if (Array.isArray(v)) init[k] = v;
      }
    } catch (_) { /* ignore */ }
    if (urlFilters.current && urlFilters.current.w) {
      for (const [k, v] of Object.entries(urlFilters.current.w)) if (Array.isArray(v)) init[k] = v;
      urlFilters.current = { ...urlFilters.current, w: null };
    }
    setWidgetFilters(init);
  }, [dashId, dash ? dash.widgets.map((w) => w.id).join(',') : '']);  // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selections so they survive a reload (per dashboard).
  useEffect(() => {
    if (!dash) return;
    try { localStorage.setItem(lsKey, JSON.stringify(filterValues)); } catch (_) { /* ignore */ }
  }, [JSON.stringify(filterValues)]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dash) return;
    try { localStorage.setItem(wfLsKey, JSON.stringify(widgetFilters)); } catch (_) { /* ignore */ }
  }, [JSON.stringify(widgetFilters)]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the URL shareable: #/analytics/dashboards/<id>[?f=…]. replaceState —
  // filter tweaks shouldn't pile up history entries; clearing removes the param.
  useEffect(() => {
    if (embedded) return;
    const base = dashHashBase(dashId);
    // Empty selections (cleared text, empty multi-select, blank ranges) don't
    // belong in a shared link — prune them so cleared filters drop the param.
    const pruned = {};
    for (const [k, v] of Object.entries(filterValues || {})) {
      if (v == null || v === '') continue;
      if (Array.isArray(v) && !v.length) continue;
      if (typeof v === 'object' && !Array.isArray(v) && Object.values(v).every((x) => x == null || x === '')) continue;
      pruned[k] = v;
    }
    const wPruned = {};
    for (const [k, v] of Object.entries(widgetFilters || {})) {
      if (Array.isArray(v) && v.length) wPruned[k] = v;
    }
    // v2 envelope only when widget filters are present — dashboard-only links
    // keep the legacy plain shape (older builds still decode them).
    const next = Object.keys(wPruned).length
      ? `${base}?f=${encodeURIComponent(JSON.stringify({ v: 2, d: pruned, w: wPruned }))}`
      : Object.keys(pruned).length
        ? `${base}?f=${encodeURIComponent(JSON.stringify(pruned))}` : base;
    if ((window.location.hash || '').slice(1) !== next) {
      try { history.replaceState(null, '', '#' + next); } catch (_) { window.location.hash = next; }
    }
  }, [dashId, JSON.stringify(filterValues), JSON.stringify(widgetFilters), embedded]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the canvas hands the URL back to the plain section route.
  useEffect(() => {
    if (embedded) return;
    return () => {
      if ((window.location.hash || '').startsWith('#' + dashHashBase(dashId))) {
        try { history.replaceState(null, '', '#/analytics'); } catch (_) { /* ignore */ }
      }
    };
  }, [dashId, embedded]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      window.pushToast({ type: 'success', title: 'Link copied', body: 'Opens this dashboard with the current filters.' });
    } catch (e) { window.pushToast({ type: 'error', title: 'Copy failed', body: _err(e) }); }
  };

  // Turn the viewer's filter-control values into the {column, op, value,
  // widget_ids} list applied to widget runs. widget_ids (when set) restricts a
  // filter to specific widgets — gated per widget in WidgetCard.
  const activeFilters = useMemo(() => {
    const out = [];
    for (const f of filterDefs) {
      const v = filterValues[f.id];
      const wid = (Array.isArray(f.widget_ids) && f.widget_ids.length) ? f.widget_ids : null;
      const push = (op, value) => out.push({ column: f.column, op, value, widget_ids: wid });
      if (f.type === 'date') {
        const from = v && v.from, to = v && v.to;
        if (from || to) push('between', [from || '0001-01-01', to || '9999-12-31']);
      } else if (f.type === 'asof') {
        if (v) push('=', v);
      } else if (f.type === 'select') {
        if (f.multi) { if (Array.isArray(v) && v.length) push('in', v); }
        else if (v != null && v !== '') push(f.op || '=', v);
      } else if (f.type === 'number') {
        const hasMin = v && v.min != null && v.min !== '', hasMax = v && v.max != null && v.max !== '';
        if (hasMin && hasMax) push('between', [Number(v.min), Number(v.max)]);
        else if (hasMin) push(['>', '>=', '='].includes(f.op) ? f.op : '>=', Number(v.min));
        else if (hasMax) push('<=', Number(v.max));
      } else if (f.type === 'boolean') {
        if (v === true || v === false) push('=', v);
      } else if (f.type === 'scope') {
        if (v === 'me' && scopeMe) push('in', [scopeMe]);
        else if (v === 'team' && scopeTeam.length) push('in', scopeTeam);
      } else if (v != null && String(v).trim()) {
        push(f.op || 'contains', String(v).trim());
      }
    }
    return out;
  }, [JSON.stringify(filterDefs), JSON.stringify(filterValues), scopeMe, JSON.stringify(scopeTeam)]);

  // Click-to-filter: clicking a chart element / table row can set a matching
  // dashboard filter (re-scoping every widget). Keyed by the filter's column.
  const crossFilterCols = useMemo(() => {
    const m = {}; for (const f of filterDefs) if (!m[f.column]) m[f.column] = f; return m;
  }, [JSON.stringify(filterDefs)]);
  const applyCrossFilter = useCallback((column, value) => {
    const def = crossFilterCols[column];
    if (!def || value == null) return false;
    setFilterValues((prev) => {
      if (def.type === 'select' && def.multi) {
        const cur = Array.isArray(prev[def.id]) ? prev[def.id] : [];
        return cur.map(String).includes(String(value)) ? prev : { ...prev, [def.id]: [...cur, value] };
      }
      return { ...prev, [def.id]: value };
    });
    if (window.pushToast) window.pushToast({ type: 'success', title: `Filtered: ${def.label}`, body: String(value) });
    return true;
  }, [JSON.stringify(filterDefs)]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Candidate columns for the filter editor — output columns, manual-query
  // columns, AND curated widgets' declared `filterable` keys (which can be
  // pushed down even though they never appear in the result).
  const candidates = useMemo(() => {
    const s = new Set();
    for (const w of (dash ? dash.widgets : [])) {
      const sp = w.spec || {};
      if (sp.x) s.add(sp.x);
      if (sp.series) s.add(sp.series);
      (sp.y || []).forEach((y) => s.add(y));
      ((sp.query && sp.query.dimensions) || []).forEach((d) => s.add(d));
      ((sp.query && sp.query.filters) || []).forEach((f) => { if (f && f.column) s.add(f.column); });
      (sp.filterable || []).forEach((fc) => { if (fc && fc.key) s.add(fc.key); });
    }
    return [...s].sort();
  }, [dash]);

  const removeWidget = async (w) => {
    const ok = await window.showConfirm({ title: 'Remove widget?', body: `Remove “${w.title}” from this dashboard?`, confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await window.AtlasApi.analytics.deleteWidget(dashId, w.id); load(); }
    catch (e) { window.pushToast({ type: 'error', title: 'Remove failed', body: _err(e) }); }
  };

  const onDrop = async (toIdx) => {
    const from = dragIdx.current;
    setDragging(null); dragIdx.current = null;
    if (from == null || from === toIdx || !dash) return;
    const ws = [...dash.widgets];
    const [moved] = ws.splice(from, 1);
    ws.splice(toIdx, 0, moved);
    setDash({ ...dash, widgets: ws });   // optimistic
    try { await Promise.all(ws.map((w, i) => window.AtlasApi.analytics.updateWidget(dashId, w.id, { display_order: i }))); }
    catch (e) { window.pushToast({ type: 'error', title: 'Reorder failed', body: _err(e) }); load(); }
  };

  const resizeWidget = async (w, delta) => {
    const cur = widgetSpan(w);
    const next = Math.min(4, Math.max(1, cur + delta));
    if (next === cur || !dash) return;
    setDash({ ...dash, widgets: dash.widgets.map((x) => x.id === w.id ? { ...x, layout: { ...(x.layout || {}), w: next } } : x) });  // optimistic
    try { await window.AtlasApi.analytics.updateWidget(dashId, w.id, { layout: { ...(w.layout || {}), w: next } }); }
    catch (e) { window.pushToast({ type: 'error', title: 'Resize failed', body: _err(e) }); load(); }
  };

  // Height mirror of resizeWidget: layout.h ∈ 1..3 (rows on the canvas grid).
  const resizeWidgetH = async (w, delta) => {
    const cur = widgetRows(w);
    const next = Math.min(3, Math.max(1, cur + delta));
    if (next === cur || !dash) return;
    setDash({ ...dash, widgets: dash.widgets.map((x) => x.id === w.id ? { ...x, layout: { ...(x.layout || {}), h: next } } : x) });  // optimistic
    try { await window.AtlasApi.analytics.updateWidget(dashId, w.id, { layout: { ...(w.layout || {}), h: next } }); }
    catch (e) { window.pushToast({ type: 'error', title: 'Resize failed', body: _err(e) }); load(); }
  };

  if (error) return <div style={{ padding: 24, color: 'var(--rose-fg)', fontSize: 13 }}>{error} {onBack && <window.Btn size="sm" variant="ghost" onClick={onBack}>Back</window.Btn>}</div>;
  if (!dash) return <div style={{ padding: 16 }}><window.SkeletonRows rows={3} /></div>;

  return (
    <div style={embedded
      ? { display: 'flex', flexDirection: 'column' }
      : { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {!embedded && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <window.Btn size="sm" variant="ghost" icon={<window.Icon.ChevronL size={13} />} onClick={onBack}>Dashboards</window.Btn>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{dash.name}</span>
        {canEdit && (
          <button onClick={() => setEditMeta(true)} title="Edit dashboard name & description" aria-label="Edit dashboard name and description"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', padding: 2, display: 'inline-flex' }}>
            <window.Icon.Edit size={13} />
          </button>
        )}
        <window.Pill hue={VISIBILITY_HUE[dash.visibility]} sz="sm">{VISIBILITY_LABEL[dash.visibility]}</window.Pill>
        {!canEdit && <window.Pill hue="slate" sz="sm">read-only{dash.owner_name ? ` · ${dash.owner_name}` : ''}</window.Pill>}
        <div style={{ flex: 1 }} />
        {dash.widgets.length > 0 && (
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Refresh size={12} />}
            onClick={() => setRefreshNonce((n) => n + 1)} title="Re-run every widget">Refresh all</window.Btn>
        )}
        {canEdit && <>
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Filter size={12} />} onClick={() => setEditFilters(true)}>Filters</window.Btn>
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Users size={12} />} onClick={() => onShare(dash, load)}>Share</window.Btn>
          <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => onAddWidget(dashId)}>Add widget</window.Btn>
        </>}
      </div>
      )}

      {filterDefs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '9px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', flexWrap: 'wrap' }}>
          {filterDefs.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>{f.label}</span>
              <FilterControl def={f} value={filterValues[f.id]} onChange={(val) => setFilterValues((prev) => ({ ...prev, [f.id]: val }))} />
            </div>
          ))}
          {activeFilters.length > 0 && <window.Btn size="xs" variant="ghost" onClick={() => setFilterValues({})}>Clear all</window.Btn>}
          {!embedded && (
            <window.Btn size="xs" variant="ghost" icon={<window.Icon.Copy size={11} />} onClick={copyLink}
              title="Copy a link that opens this dashboard with the current filters">Copy link</window.Btn>
          )}
        </div>
      )}

      <div style={embedded ? { padding: 16 } : { flex: 1, overflow: 'auto', padding: 16 }}>
        {dash.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>{dash.description}</div>}
        {dash.widgets.length === 0 ? (
          <EmptyState icon="ChartBar" title="No widgets yet"
            sub="This dashboard has no widgets yet."
            action={canEdit ? <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => onAddWidget(dashId)}>Add widget</window.Btn> : null} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, alignItems: 'start' }}>
            {dash.widgets.map((w, i) => (
              <WidgetCard key={w.id} dashId={dashId} widget={w} canEdit={canEdit} filters={activeFilters}
                embedded={embedded} widgetFilters={widgetFilters[w.id] || []}
                onWidgetFilters={(arr) => setWidgetFilters((prev) => ({ ...prev, [w.id]: arr }))}
                span={widgetSpan(w)} onResize={(delta) => resizeWidget(w, delta)}
                rows={widgetRows(w)} onResizeH={(delta) => resizeWidgetH(w, delta)}
                refreshNonce={refreshNonce} onChanged={load}
                dragging={dragging === i}
                crossFilterCols={crossFilterCols} onCrossFilter={applyCrossFilter}
                onRemove={() => removeWidget(w)}
                onDragStart={() => { dragIdx.current = i; setDragging(i); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onDrop(i); }} />
            ))}
          </div>
        )}
      </div>

      {editFilters && (
        <FiltersDialog dash={dash} candidates={candidates} widgets={dash.widgets} onClose={() => setEditFilters(false)}
          onSaved={() => { setEditFilters(false); load(); }} />
      )}
      {editMeta && (
        <DashboardMetaDialog dash={dash} onClose={() => setEditMeta(false)}
          onSaved={() => { setEditMeta(false); load(); }} />
      )}
    </div>
  );
};

// ── Dashboard name + description dialog (canvas header pencil) ───────────────
const DashboardMetaDialog = ({ dash, onClose, onSaved }) => {
  const [name, setName] = useState(dash.name || '');
  const [description, setDescription] = useState(dash.description || '');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  window.useFocusTrap(true, ref);
  window.useEscapeKey(true, onClose);
  const submit = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      // description is always sent — '' clears it (the PATCH treats null as "leave unchanged").
      await window.AtlasApi.analytics.updateDashboard(dash.id, { name: name.trim(), description: description.trim() });
      onSaved();
    } catch (e) { window.pushToast({ type: 'error', title: 'Update failed', body: _err(e) }); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'oklch(0.2 0.01 270 / 0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={ref} onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', width: 420, maxWidth: '92vw', padding: '16px 18px' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Edit dashboard</h3>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>Name</div>
        <window.TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, margin: '10px 0 4px' }}>Description</div>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          placeholder="What is this dashboard for? (optional)"
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 7, padding: '7px 9px', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
          <window.Btn size="sm" variant="accent" onClick={submit} disabled={busy || !name.trim()}>{busy ? 'Saving…' : 'Save'}</window.Btn>
        </div>
      </div>
    </div>
  );
};

// ── Small centered edit dialog (rename / move-to-group / rename-group) ───────
const EditDialog = ({ title, label, initial, chips, placeholder, submitLabel = 'Save', onSubmit, onClose }) => {
  const [val, setVal] = useState(initial == null ? '' : initial);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  window.useFocusTrap(true, ref);
  window.useEscapeKey(true, onClose);
  const submit = async () => { setBusy(true); try { await onSubmit(val.trim()); } finally { setBusy(false); } };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'oklch(0.2 0.01 270 / 0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={ref} onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', width: 380, maxWidth: '92vw', padding: '16px 18px' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{title}</h3>
        {label && <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>{label}</div>}
        <window.TextInput value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        {chips && chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
            {chips.map((c) => (
              <button key={c} onClick={() => setVal(c)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer' }}>{c}</button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
          <window.Btn size="sm" variant="accent" onClick={submit} disabled={busy}>{busy ? 'Saving…' : submitLabel}</window.Btn>
        </div>
      </div>
    </div>
  );
};

// Keyboard activation for the clickable cards (plain divs otherwise).
const cardKeyDown = (fn) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
};

// ── One dashboard card (with a ⋯ menu for owners/admins) ─────────────────────
const DashboardCard = ({ d, onOpen, onRename, onMove, onShare, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menu]);
  return (
    <div onClick={() => onOpen(d.id)} role="button" tabIndex={0} onKeyDown={cardKeyDown(() => onOpen(d.id))} style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
      padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 96,
    }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <window.Icon.Grid size={15} color="var(--accent)" />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
        {d.can_edit && (
          <div ref={ref} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button onClick={() => setMenu((v) => !v)} aria-label={`Dashboard actions: ${d.name}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
              <window.Icon.More size={14} />
            </button>
            {menu && (
              <div style={{ position: 'absolute', right: 0, top: 22, zIndex: 30, minWidth: 160, background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 7, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
                <MenuItem icon={<window.Icon.Edit size={12} />} label="Rename" onClick={() => { setMenu(false); onRename(d); }} />
                <MenuItem icon={<window.Icon.Folder size={12} />} label="Move to group…" onClick={() => { setMenu(false); onMove(d); }} />
                <MenuItem icon={<window.Icon.Users size={12} />} label="Share…" onClick={() => { setMenu(false); onShare(d); }} />
                <MenuItem icon={<window.Icon.Trash size={12} />} label="Delete" danger onClick={() => { setMenu(false); onDelete(d); }} />
              </div>
            )}
          </div>
        )}
      </div>
      {d.description && <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.4 }}>{d.description}</div>}
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <window.Pill hue={VISIBILITY_HUE[d.visibility]} sz="sm">{VISIBILITY_LABEL[d.visibility]}</window.Pill>
        {d.group_name && <window.Pill hue="slate" sz="sm">{d.group_name}</window.Pill>}
        <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{d.widget_count} widget{d.widget_count === 1 ? '' : 's'}</span>
        <div style={{ flex: 1 }} />
        {!d.can_edit && d.owner_name && <span style={{ fontSize: 10, color: 'var(--text-4)' }}>by {d.owner_name}</span>}
      </div>
    </div>
  );
};

// ── One saved-view card (same shape as DashboardCard; opens the view page) ───
const VIEW_ENTITY_LABEL = { companies: 'Companies', contacts: 'People', engagements: 'Engagements', notes: 'Notes' };
const ViewCard = ({ v, onOpen, onRename, onMove, onShare, onDuplicate, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menu]);
  return (
    <div onClick={() => onOpen(v.id)} role="button" tabIndex={0} onKeyDown={cardKeyDown(() => onOpen(v.id))} style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
      padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 96,
    }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <window.Icon.Table size={15} color="var(--accent)" />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
        <div ref={ref} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <button onClick={() => setMenu((x) => !x)} aria-label={`View actions: ${v.name}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
            <window.Icon.More size={14} />
          </button>
          {menu && (
            <div style={{ position: 'absolute', right: 0, top: 22, zIndex: 30, minWidth: 160, background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 7, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
              {v.can_edit && <MenuItem icon={<window.Icon.Edit size={12} />} label="Rename" onClick={() => { setMenu(false); onRename(v); }} />}
              {v.can_edit && <MenuItem icon={<window.Icon.Folder size={12} />} label="Move to group…" onClick={() => { setMenu(false); onMove(v); }} />}
              {v.can_edit && <MenuItem icon={<window.Icon.Users size={12} />} label="Share…" onClick={() => { setMenu(false); onShare(v); }} />}
              {/* Duplicate needs view access only — copy a teammate's shared view. */}
              <MenuItem icon={<window.Icon.Copy size={12} />} label="Duplicate" onClick={() => { setMenu(false); onDuplicate(v); }} />
              {v.can_edit && <MenuItem icon={<window.Icon.Trash size={12} />} label="Delete" danger onClick={() => { setMenu(false); onDelete(v); }} />}
            </div>
          )}
        </div>
      </div>
      {v.description && <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{v.description}</div>}
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <window.Pill hue={VISIBILITY_HUE[v.visibility]} sz="sm">{VISIBILITY_LABEL[v.visibility]}</window.Pill>
        <window.Pill hue="slate" sz="sm">{VIEW_ENTITY_LABEL[v.source_entity] || v.source_entity}</window.Pill>
        <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>view</span>
        <div style={{ flex: 1 }} />
        {!v.can_edit && v.owner_name && <span style={{ fontSize: 10, color: 'var(--text-4)' }}>by {v.owner_name}</span>}
      </div>
    </div>
  );
};

// Sub-section divider used when a folder holds both dashboards and views.
const SectionLabel = ({ label, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 10px' }}>
    <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
    <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{count}</span>
    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
  </div>
);

// ── Library browser: left rail of folders + filtered gallery of dashboards
// and saved views sharing one folder namespace ────────────────────────────────
const DashboardsBrowser = ({ onOpen, onNew, onOpenView, onNewView }) => {
  const [dashboards, setDashboards] = useState(null);
  const [views, setViews] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('__all__');  // '__all__' | '__ungrouped__' | <name>
  const [edit, setEdit] = useState(null);
  const [query, setQuery] = useState('');
  // Folders are implicit (a nullable group_name on each item), so a brand-new
  // EMPTY folder can't exist server-side. "+ New folder" keeps the name here —
  // ephemeral, local component state — so the rail shows it and New view /
  // New dashboard / Move can target it; it persists once an item names it.
  const [localFolders, setLocalFolders] = useState([]);
  const [shareDash, setShareDash] = useState(null);   // full dashboard detail for ShareDialog
  const [shareView, setShareView] = useState(null);   // full view detail for window.ShareViewDialog

  const reload = useCallback(() => {
    window.AtlasApi.analytics.listDashboards()
      .then((d) => setDashboards(Array.isArray(d) ? d : [])).catch(() => setDashboards([]));
    window.AtlasApi.views.list()
      .then((v) => setViews(Array.isArray(v) ? v : [])).catch(() => setViews([]));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const groups = useMemo(() => {
    const counts = new Map();
    let ungrouped = 0;
    [...(dashboards || []), ...(views || [])].forEach((d) => {
      if (d.group_name) counts.set(d.group_name, (counts.get(d.group_name) || 0) + 1);
      else ungrouped += 1;
    });
    // Merge the ephemeral just-created folders in (count 0 until an item lands).
    localFolders.forEach((n) => { if (!counts.has(n)) counts.set(n, 0); });
    return { named: [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0])), ungrouped, total: (dashboards || []).length + (views || []).length };
  }, [dashboards, views, localFolders]);
  const groupNames = useMemo(() => groups.named.map(([n]) => n), [groups]);

  const inGroup = (d) =>
    selectedGroup === '__all__' ? true
      : selectedGroup === '__ungrouped__' ? !d.group_name
        : d.group_name === selectedGroup;
  const q = query.trim().toLowerCase();
  const matches = (x) => !q || (x.name || '').toLowerCase().includes(q);
  const filtered = (dashboards || []).filter(inGroup).filter(matches);
  const filteredViews = (views || []).filter(inGroup).filter(matches);

  const doRename = (d) => setEdit({
    title: 'Rename dashboard', label: 'Name', initial: d.name,
    onSubmit: async (v) => { if (v) await window.AtlasApi.analytics.updateDashboard(d.id, { name: v }); setEdit(null); reload(); },
  });
  const doMove = (d) => setEdit({
    title: 'Move to group', label: 'Group (blank = Ungrouped)', initial: d.group_name || '',
    chips: groupNames, placeholder: 'e.g. Sales',
    onSubmit: async (v) => { await window.AtlasApi.analytics.updateDashboard(d.id, { group_name: v || null }); setEdit(null); reload(); if (v && selectedGroup !== '__all__') setSelectedGroup(v); },
  });
  const doDelete = async (d) => {
    const ok = await window.showConfirm({ title: 'Delete dashboard?', body: `Delete “${d.name}”? This removes it and its widgets.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { await window.AtlasApi.analytics.deleteDashboard(d.id); reload(); }
    catch (e) { window.pushToast({ type: 'error', title: 'Delete failed', body: _err(e) }); }
  };
  // Server-side folder rename/ungroup across BOTH namespaces (dashboards +
  // views share the folder rail). Each endpoint scopes itself to rows the
  // caller may edit and reports {renamed, skipped} — no client fan-out, no
  // silent partial renames.
  const renameGroupEverywhere = async (name, target) => {
    let renamed = 0, skipped = 0;
    const add = (r) => { renamed += (r && r.renamed) || 0; skipped += (r && r.skipped) || 0; };
    if ((dashboards || []).some((d) => d.group_name === name)) {
      add(await window.AtlasApi.analytics.renameGroup({ old_name: name, new_name: target }));
    }
    if ((views || []).some((x) => x.group_name === name)) {
      add(await window.AtlasApi.views.renameGroup({ old_name: name, new_name: target }));
    }
    return { renamed, skipped };
  };
  const groupResultToast = (name, target, { renamed, skipped }) => {
    if (skipped > 0) {
      window.pushToast({
        type: 'warning', title: 'Partially moved',
        body: `${renamed} item${renamed === 1 ? '' : 's'} moved — ${skipped} you can't edit ${skipped === 1 ? 'was' : 'were'} left in “${name}”.`,
      });
    } else {
      window.pushToast({
        type: 'success', title: target ? `Folder renamed to “${target}”` : `Folder “${name}” removed`,
        body: `${renamed} item${renamed === 1 ? '' : 's'} updated`,
      });
    }
  };
  const doRenameGroup = (name) => setEdit({
    title: `Rename folder “${name}”`, label: 'New folder name', initial: name,
    onSubmit: async (v) => {
      const target = (v || '').trim() || null;
      try {
        const res = await renameGroupEverywhere(name, target);
        groupResultToast(name, target, res);
        setLocalFolders((prev) => prev.filter((n) => n !== name));
        setEdit(null); reload(); setSelectedGroup(res.skipped > 0 ? '__all__' : (target || '__ungrouped__'));
      } catch (e) {
        window.pushToast({ type: 'error', title: 'Rename failed', body: _err(e) });
      }
    },
  });
  const doDeleteGroup = async (name, count) => {
    const ok = await window.showConfirm({
      title: `Delete folder “${name}”?`,
      body: count > 0
        ? `Move ${count} item${count === 1 ? '' : 's'} to Ungrouped. Nothing else is deleted — only the folder label goes away.`
        : 'This folder is empty — it will just disappear.',
      confirmLabel: 'Delete folder', danger: true,
    });
    if (!ok) return;
    try {
      const res = await renameGroupEverywhere(name, null);
      groupResultToast(name, null, res);
      setLocalFolders((prev) => prev.filter((n) => n !== name));
      reload(); setSelectedGroup(res.skipped > 0 ? '__all__' : '__ungrouped__');
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Delete failed', body: _err(e) });
    }
  };
  const doNewFolder = () => setEdit({
    title: 'New folder', label: 'Folder name', placeholder: 'e.g. Sales', submitLabel: 'Create',
    onSubmit: async (v) => {
      const name = (v || '').trim();
      if (name) {
        setLocalFolders((prev) => (prev.includes(name) ? prev : [...prev, name]));
        setSelectedGroup(name);
      }
      setEdit(null);
    },
  });

  // Share dialogs need the full detail (share entries) — the list rows don't carry it.
  const doShareDash = async (d) => {
    try { setShareDash(await window.AtlasApi.analytics.getDashboard(d.id)); }
    catch (e) { window.pushToast({ type: 'error', title: "Couldn't open sharing", body: _err(e) }); }
  };
  const doShareView = async (v) => {
    try { setShareView(await window.AtlasApi.views.get(v.id)); }
    catch (e) { window.pushToast({ type: 'error', title: "Couldn't open sharing", body: _err(e) }); }
  };
  const doDuplicateView = async (v) => {
    try {
      const d = await window.AtlasApi.views.duplicate(v.id);
      window.pushToast({ type: 'success', title: 'View duplicated', body: d.name });
      reload();
    } catch (e) { window.pushToast({ type: 'error', title: "Couldn't duplicate", body: _err(e) }); }
  };

  const doRenameView = (v) => setEdit({
    title: 'Rename view', label: 'Name', initial: v.name,
    onSubmit: async (nv) => { if (nv) await window.AtlasApi.views.update(v.id, { name: nv }); setEdit(null); reload(); },
  });
  const doMoveView = (v) => setEdit({
    title: 'Move to group', label: 'Group (blank = Ungrouped)', initial: v.group_name || '',
    chips: groupNames, placeholder: 'e.g. Sales',
    onSubmit: async (nv) => { await window.AtlasApi.views.update(v.id, { group_name: nv || null }); setEdit(null); reload(); if (nv && selectedGroup !== '__all__') setSelectedGroup(nv); },
  });
  const doDeleteView = async (v) => {
    const ok = await window.showConfirm({ title: 'Delete view?', body: `Delete “${v.name}”?`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { await window.AtlasApi.views.remove(v.id); reload(); }
    catch (e) { window.pushToast({ type: 'error', title: 'Delete failed', body: _err(e) }); }
  };

  if (dashboards === null || views === null) return <div style={{ padding: 16 }}><window.SkeletonRows rows={3} /></div>;

  const railItem = (key, label, count, opts = {}) => {
    const active = selectedGroup === key;
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', borderRadius: 6, background: active ? 'var(--surface-hover)' : 'transparent' }}>
        <button onClick={() => setSelectedGroup(key)} style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', border: 'none', background: 'transparent',
          cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: active ? 'var(--text)' : 'var(--text-2)', fontWeight: active ? 600 : 500, minWidth: 0,
        }}>
          {opts.icon || <window.Icon.Folder size={13} color={active ? 'var(--accent)' : 'var(--text-3)'} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{count}</span>
        </button>
        {opts.onRename && (
          <button onClick={opts.onRename} title="Rename folder" aria-label={`Rename folder ${label}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', padding: '0 3px' }}>
            <window.Icon.Edit size={11} />
          </button>
        )}
        {opts.onDelete && (
          <button onClick={opts.onDelete} title="Delete folder (moves items to Ungrouped)" aria-label={`Delete folder ${label}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', padding: '0 6px 0 3px' }}>
            <window.Icon.Trash size={11} />
          </button>
        )}
      </div>
    );
  };

  const heading = selectedGroup === '__all__' ? 'All' : selectedGroup === '__ungrouped__' ? 'Ungrouped' : selectedGroup;
  const newGroupArg = (selectedGroup === '__all__' || selectedGroup === '__ungrouped__') ? null : selectedGroup;

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border)', overflow: 'auto', padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, padding: '0 8px 6px' }}>Folders</div>
        {railItem('__all__', 'All', groups.total, { icon: <window.Icon.List size={13} color={selectedGroup === '__all__' ? 'var(--accent)' : 'var(--text-3)'} /> })}
        {groups.named.map(([name, count]) => railItem(name, name, count, { onRename: () => doRenameGroup(name), onDelete: () => doDeleteGroup(name, count) }))}
        {groups.ungrouped > 0 && railItem('__ungrouped__', 'Ungrouped', groups.ungrouped, { icon: <window.Icon.Doc size={13} color={selectedGroup === '__ungrouped__' ? 'var(--accent)' : 'var(--text-3)'} /> })}
        <button onClick={doNewFolder} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', marginTop: 4, border: 'none', background: 'transparent',
          cursor: 'pointer', textAlign: 'left', fontSize: 12, color: 'var(--text-3)', borderRadius: 6,
        }}>
          <window.Icon.Plus size={12} /> New folder
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{heading}</span>
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-4)' }}>{filtered.length + filteredViews.length}</span>
          <div style={{ flex: 1 }} />
          <window.TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search library…"
            aria-label="Search dashboards and views" style={{ width: 190, marginRight: 8 }} />
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Plus size={12} />} onClick={() => onNewView(newGroupArg)}>New view</window.Btn>
          <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => onNew(newGroupArg)} style={{ marginLeft: 8 }}>New dashboard</window.Btn>
        </div>
        {filtered.length === 0 && filteredViews.length === 0 ? (
          q ? (
            <EmptyState icon="Search" title="No matches" sub={`Nothing named “${query.trim()}” here.`} />
          ) : (
            <EmptyState icon="Grid" title="Nothing here yet"
              sub="Create a dashboard or a view to fill this folder."
              action={<div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => onNewView(newGroupArg)}>New view</window.Btn>
                <window.Btn size="sm" variant="outline" icon={<window.Icon.Plus size={12} />} onClick={() => onNew(newGroupArg)}>New dashboard</window.Btn>
              </div>} />
          )
        ) : (
          <>
            {filtered.length > 0 && (
              <>
                {filteredViews.length > 0 && <SectionLabel label="Dashboards" count={filtered.length} />}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {filtered.map((d) => (
                    <DashboardCard key={d.id} d={d} onOpen={onOpen} onRename={doRename} onMove={doMove} onShare={doShareDash} onDelete={doDelete} />
                  ))}
                </div>
              </>
            )}
            {filteredViews.length > 0 && (
              <>
                {filtered.length > 0 && <SectionLabel label="Views" count={filteredViews.length} />}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {filteredViews.map((v) => (
                    <ViewCard key={v.id} v={v} onOpen={onOpenView} onRename={doRenameView} onMove={doMoveView}
                      onShare={doShareView} onDuplicate={doDuplicateView} onDelete={doDeleteView} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {edit && (
        <EditDialog title={edit.title} label={edit.label} initial={edit.initial} chips={edit.chips}
          placeholder={edit.placeholder} submitLabel={edit.submitLabel} onSubmit={edit.onSubmit} onClose={() => setEdit(null)} />
      )}
      {shareDash && (
        <ShareDialog open onClose={() => setShareDash(null)} dashboard={shareDash}
          onSaved={() => { setShareDash(null); reload(); }} />
      )}
      {/* ShareViewDialog lives in saved_views.jsx — esbuild modules are isolated, so it crosses files via window.* */}
      {shareView && window.ShareViewDialog && (
        <window.ShareViewDialog view={shareView} onClose={() => setShareView(null)}
          onSaved={() => { setShareView(null); reload(); }} />
      )}
    </div>
  );
};

// ── Manual query builder (structured, no AI) ─────────────────────────────────
const AGG_OPTIONS = [
  { value: 'count', label: 'Count (rows)' },
  { value: 'count_distinct', label: 'Count distinct' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
];
const OPS_BY_CAT = {
  number: ['=', '!=', '>', '>=', '<', '<=', 'between', 'is_null', 'is_not_null'],
  date: ['=', '!=', '>', '>=', '<', '<=', 'between', 'is_null', 'is_not_null'],
  boolean: ['=', 'is_null', 'is_not_null'],
  text: ['=', '!=', 'contains', 'starts_with', 'in', 'not_in', 'is_null', 'is_not_null'],
};
const OP_LABEL = { '=': '=', '!=': '≠', '>': '>', '>=': '≥', '<': '<', '<=': '≤', between: 'between', contains: 'contains', starts_with: 'starts with', in: 'in list', not_in: 'not in list', is_null: 'is empty', is_not_null: 'is not empty' };
const measureAlias = (m) => (m.agg === 'count' && !m.column) ? 'count' : m.agg === 'count_distinct' ? `count_distinct_${m.column}` : `${m.agg}_${m.column}`;
const FieldLabel = ({ children }) => (
  <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 5 }}>{children}</div>
);

// ── Manual builder: friendly labels + smart filter-value pickers ─────────────
const _hc = (s) => (window.humanizeCol ? window.humanizeCol(s) : s);
const sourceLabel = (s) => (s.recommended ? '★ ' : '') + _hc(String(s.name).replace(/^v_/, ''));

const FIELD_CATS = [['text', 'Text'], ['number', 'Number'], ['date', 'Date'], ['boolean', 'Yes / no']];
// Grouped + humanized column options for the field pickers (uses Combobox headers).
const groupedColOpts = (cols, opts = {}) => {
  const ex = new Set(opts.exclude || []);
  const out = [];
  for (const [cat, label] of FIELD_CATS) {
    if (opts.only && cat !== opts.only) continue;
    const items = (cols || []).filter((c) => (c.category || 'text') === cat && !ex.has(c.name));
    if (!items.length) continue;
    out.push({ header: label });
    items.forEach((c) => out.push({ value: c.name, label: _hc(c.name) }));
  }
  return out;
};

const DateInput = (props) => (
  <input type="date" {...props} value={props.value || ''}
    style={{ flex: 1, width: '100%', padding: '5px 7px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text)', ...(props.style || {}) }} />
);

// Single value picker fed by the column's distinct values; falls back to free
// text. `fetchValues` overrides the endpoint (the Views builder passes its
// any-role mirror); defaults to the admin analytics one.
const ValueCombo = ({ source, column, value, onChange, style, fetchValues }) => {
  const [vals, setVals] = useState(null);
  useEffect(() => {
    let live = true;
    (fetchValues || window.AtlasApi.analytics.values)(source, column)
      .then((v) => { if (live) setVals(Array.isArray(v) ? v : []); })
      .catch(() => { if (live) setVals([]); });
    return () => { live = false; };
  }, [source, column]);
  if (vals === null) return <window.TextInput value={value || ''} placeholder="loading…" disabled style={style} />;
  if (!vals.length) return <window.TextInput value={value || ''} placeholder="value" onChange={(e) => onChange(e.target.value)} style={style} />;
  const opts = vals.map((v) => ({ value: String(v.value), label: String(v.value) }));
  if (value != null && value !== '' && !opts.some((o) => o.value === String(value))) opts.unshift({ value: String(value), label: String(value) });
  return <window.Combobox value={value == null ? '' : String(value)} searchable placeholder="Pick a value" onChange={(e) => onChange(e.target.value)} options={opts} style={style} />;
};

// Chip multi-select for in / not-in, fed by the same value list.
const MultiValueCombo = ({ source, column, values, onChange, fetchValues }) => {
  const [vals, setVals] = useState(null);
  useEffect(() => {
    let live = true;
    (fetchValues || window.AtlasApi.analytics.values)(source, column)
      .then((v) => { if (live) setVals(Array.isArray(v) ? v : []); })
      .catch(() => { if (live) setVals([]); });
    return () => { live = false; };
  }, [source, column]);
  const sel = Array.isArray(values) ? values : [];
  const opts = (vals || []).map((v) => ({ value: String(v.value), label: String(v.value) })).filter((o) => !sel.includes(o.value));
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      {sel.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {sel.map((v) => (
            <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '1px 5px 1px 8px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
              {v}<button onClick={() => onChange(sel.filter((x) => x !== v))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      {(vals === null || opts.length > 0) && (
        <window.Combobox value="" searchable placeholder={vals === null ? 'loading…' : '+ Add value'}
          onChange={(e) => { if (e.target.value) onChange([...sel, e.target.value]); }} options={opts} />
      )}
    </div>
  );
};

// The right value control for a filter, by column category + operator.
const FilterValueInput = ({ source, column, cat, op, f, setF, fetchValues }) => {
  if (op === 'is_null' || op === 'is_not_null') return null;
  if (cat === 'boolean') {
    return <window.Combobox value={String(f.value)} onChange={(e) => setF({ value: e.target.value })}
      options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]} style={{ flex: 1 }} />;
  }
  if (cat === 'date') {
    if (op === 'between') return (<><DateInput value={f.value} onChange={(e) => setF({ value: e.target.value })} /><DateInput value={f.value2} onChange={(e) => setF({ value2: e.target.value })} /></>);
    return <DateInput value={f.value} onChange={(e) => setF({ value: e.target.value })} />;
  }
  if (cat === 'number') {
    const num = (e) => e.target.value.replace(/[^\d.\-]/g, '');
    if (op === 'between') return (<>
      <window.TextInput value={f.value || ''} placeholder="min" onChange={(e) => setF({ value: num(e) })} style={{ flex: 1 }} />
      <window.TextInput value={f.value2 || ''} placeholder="max" onChange={(e) => setF({ value2: num(e) })} style={{ flex: 1 }} />
    </>);
    return <window.TextInput value={f.value || ''} placeholder="number" onChange={(e) => setF({ value: num(e) })} style={{ flex: 1 }} />;
  }
  if (op === 'in' || op === 'not_in') return <MultiValueCombo source={source} column={column} values={f.value} onChange={(arr) => setF({ value: arr })} fetchValues={fetchValues} />;
  if (op === 'contains' || op === 'starts_with') return <window.TextInput value={f.value || ''} placeholder="text…" onChange={(e) => setF({ value: e.target.value })} style={{ flex: 1 }} />;
  return <ValueCombo source={source} column={f.column || column} value={f.value} onChange={(v) => setF({ value: v })} style={{ flex: 1 }} fetchValues={fetchValues} />;
};

const ManualBuilder = ({ targetGroup, onSavedToDashboard }) => {
  const [sources, setSources] = useState(null);
  const [source, setSource] = useState('');
  const [dims, setDims] = useState([]);
  const [measures, setMeasures] = useState([{ agg: 'count', column: '' }]);
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState({ by: '', dir: 'desc' });
  const [limit, setLimit] = useState(50);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [spec, setSpec] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [drill, setDrill] = useState(null);
  const [listMode, setListMode] = useState(false);   // false = aggregate, true = editable record list

  useEffect(() => {
    window.AtlasApi.analytics.sources()
      .then((s) => { const arr = Array.isArray(s) ? s : []; setSources(arr); if (arr[0]) setSource(arr[0].name); })
      .catch(() => setSources([]));
  }, []);

  const src = (sources || []).find((s) => s.name === source) || null;
  const cols = src ? src.columns : [];
  // Lookup (FK→name) display columns — the compiler resolves them to correlated
  // subqueries, so they work as group-bys / list columns / filters (not measures).
  const lookups = src ? (src.lookups || []) : [];
  const dimCols = useMemo(() => [...cols, ...lookups], [cols, lookups]);
  const colCat = useMemo(() => Object.fromEntries(dimCols.map((c) => [c.name, c.category])), [dimCols]);
  const colOpts = cols.map((c) => ({ value: c.name, label: c.name }));
  const aliases = measures.map(measureAlias);
  const sortOpts = [{ value: '', label: 'Default' }, ...[...dims, ...aliases].map((x) => ({ value: x, label: _hc(x) }))];

  const changeSource = (name) => { setSource(name); setDims([]); setMeasures([{ agg: 'count', column: '' }]); setFilters([]); setSort({ by: '', dir: 'desc' }); setResult(null); setSpec(null); setError(''); };
  const coerce = (v, cat) => cat === 'number' ? Number(v) : cat === 'boolean' ? (v === true || v === 'true') : v;

  const run = async () => {
    setRunning(true); setError('');
    try {
      const model = {
        source,
        mode: listMode ? 'list' : 'aggregate',
        dimensions: dims,
        columns: listMode ? dims : [],
        measures: listMode ? [] : measures.map((m) => (m.agg === 'count' && !m.column) ? { agg: 'count' } : { agg: m.agg, column: m.column || null }),
        filters: filters.filter((f) => f.column && f.op).map((f) => {
          const cat = colCat[f.column] || 'text';
          if (f.op === 'is_null' || f.op === 'is_not_null') return { column: f.column, op: f.op, value: null };
          if (f.op === 'between') return { column: f.column, op: 'between', value: [coerce(f.value, cat), coerce(f.value2, cat)] };
          if (f.op === 'in' || f.op === 'not_in') return { column: f.column, op: f.op, value: Array.isArray(f.value) ? f.value : String(f.value || '').split(',').map((s) => s.trim()).filter(Boolean) };
          return { column: f.column, op: f.op, value: coerce(f.value, cat) };
        }),
        sort: sort.by ? sort : null,
        limit: Number(limit) || 50,
      };
      const res = await window.AtlasApi.analytics.build(model);
      setResult(res); setSpec(res.spec);
    } catch (e) { setError(_err(e)); } finally { setRunning(false); }
  };

  const measureColOpts = (m) => (m.agg === 'sum' || m.agg === 'avg')
    ? groupedColOpts(cols, { only: 'number' })
    : groupedColOpts(cols);

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* config rail */}
      <div style={{ width: 340, flexShrink: 0, borderRight: '1px solid var(--border)', overflow: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <FieldLabel>Data source</FieldLabel>
          {sources === null ? <window.Skeleton w="100%" h={30} /> : (
            <window.Combobox value={source} onChange={(e) => changeSource(e.target.value)} searchable
              options={(sources || []).map((s) => ({ value: s.name, label: sourceLabel(s) }))} />
          )}
        </div>

        <div>
          <FieldLabel>View type</FieldLabel>
          <Segmented value={listMode ? 'list' : 'agg'} onChange={(v) => setListMode(v === 'list')}
            options={[{ value: 'agg', label: 'Aggregate' }, { value: 'list', label: 'Records (editable)' }]} />
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 4 }}>
            {listMode ? 'One row per record — editable fields can be changed inline.' : 'Group rows and compute measures.'}
          </div>
        </div>

        <div>
          <FieldLabel>{listMode ? 'Columns' : 'Group by'}</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: dims.length ? 6 : 0 }}>
            {dims.map((d) => (
              <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 6px 2px 9px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
                {_hc(d)}
                <button onClick={() => setDims(dims.filter((x) => x !== d))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            ))}
          </div>
          {dims.length < 4 && (
            <window.Combobox value="" placeholder={listMode ? '+ Add column' : '+ Add dimension'} searchable
              onChange={(e) => { if (e.target.value) setDims([...dims, e.target.value]); }}
              options={groupedColOpts(dimCols, { exclude: dims })} />
          )}
        </div>

        {!listMode && (
        <div>
          <FieldLabel>Measures</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {measures.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <window.Combobox value={m.agg} onChange={(e) => setMeasures(measures.map((x, j) => j === i ? { ...x, agg: e.target.value } : x))} options={AGG_OPTIONS} style={{ width: 110 }} />
                {m.agg !== 'count' && (
                  <window.Combobox value={m.column || ''} placeholder="column" searchable
                    onChange={(e) => setMeasures(measures.map((x, j) => j === i ? { ...x, column: e.target.value } : x))}
                    options={measureColOpts(m)} style={{ flex: 1 }} />
                )}
                {measures.length > 1 && <button onClick={() => setMeasures(measures.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', fontSize: 14 }}>×</button>}
              </div>
            ))}
          </div>
          {measures.length < 5 && <button onClick={() => setMeasures([...measures, { agg: 'sum', column: '' }])} style={{ marginTop: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>+ Add measure</button>}
        </div>
        )}

        <div>
          <FieldLabel>Filters</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filters.map((f, i) => {
              const cat = colCat[f.column] || 'text';
              const ops = OPS_BY_CAT[cat] || OPS_BY_CAT.text;
              const noVal = f.op === 'is_null' || f.op === 'is_not_null';
              const setF = (patch) => setFilters(filters.map((x, j) => j === i ? { ...x, ...patch } : x));
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <window.Combobox value={f.column || ''} placeholder="Field" searchable
                      onChange={(e) => { const c = e.target.value; const ncat = colCat[c] || 'text'; setF({ column: c, op: (OPS_BY_CAT[ncat] || OPS_BY_CAT.text)[0], value: '', value2: '' }); }}
                      options={groupedColOpts(dimCols)} style={{ flex: 1 }} />
                    <button onClick={() => setFilters(filters.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', fontSize: 14 }}>×</button>
                  </div>
                  {f.column && (
                    <div style={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
                      <window.Combobox value={f.op}
                        onChange={(e) => { const op = e.target.value; setF({ op, value: (op === 'in' || op === 'not_in') ? [] : '', value2: '' }); }}
                        options={ops.map((o) => ({ value: o, label: OP_LABEL[o] }))} style={{ width: 118, flexShrink: 0 }} />
                      <FilterValueInput source={source} column={f.column} cat={cat} op={f.op} f={f} setF={setF} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={() => setFilters([...filters, { column: '', op: '=', value: '', value2: '' }])} style={{ marginTop: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)' }}>+ Add filter</button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>Sort by</FieldLabel>
            <window.Combobox value={sort.by} onChange={(e) => setSort({ ...sort, by: e.target.value })} options={sortOpts} />
          </div>
          <window.Btn size="sm" variant="outline" onClick={() => setSort({ ...sort, dir: sort.dir === 'desc' ? 'asc' : 'desc' })} title="Toggle direction">{sort.dir === 'desc' ? '↓' : '↑'}</window.Btn>
          <div style={{ width: 70 }}>
            <FieldLabel>Limit</FieldLabel>
            <window.TextInput value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
        </div>

        <window.Btn variant="accent" disabled={running || !source} onClick={run} icon={<window.Icon.Play size={12} />}>
          {running ? 'Running…' : 'Run query'}
        </window.Btn>
      </div>

      {/* preview */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {error ? (
          <div style={{ background: 'var(--rose-bg)', color: 'var(--rose-fg)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', fontSize: 12.5 }}>
            <strong>Query failed.</strong> {error}
          </div>
        ) : !result || !spec ? (
          <EmptyState icon="ChartBar" title="Build a query" />
        ) : (
          <window.Section>
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input value={spec.title || ''} onChange={(e) => setSpec((s) => ({ ...s, title: e.target.value }))}
                  style={{ flex: 1, minWidth: 200, fontSize: 15, fontWeight: 600, color: 'var(--text)', border: '1px solid transparent', borderRadius: 5, padding: '3px 6px', background: 'transparent' }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--border)'} onBlur={(e) => e.target.style.borderColor = 'transparent'} />
                <span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">{result.row_count} row{result.row_count === 1 ? '' : 's'}{result.truncated ? '+' : ''}</span>
                <window.Btn size="sm" variant="outline" icon={<window.Icon.External size={12} />}
                  onClick={() => setCsvOpen(true)} title="Export as CSV — up to 10,000 rows">CSV</window.Btn>
                <window.Btn size="sm" variant="accent" icon={<window.Icon.Plus size={12} />} onClick={() => setSaveOpen(true)}>Save</window.Btn>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 12, minHeight: 200 }}>
                <window.WidgetRenderer spec={spec} data={result} height={spec.chart_type === 'table' ? undefined : 320}
                  onSelect={(sel) => setDrill({ spec, data: result, selection: sel })} />
                <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--text-4)', textAlign: 'center' }}>Tip: click any point to see the data behind it.</div>
              </div>
              <SpecEditor spec={spec} setSpec={setSpec} columns={result.columns} />
              {spec.caveats && spec.caveats.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {spec.caveats.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11.5, color: 'var(--amber-fg)', background: 'var(--amber-bg)', padding: '6px 8px', borderRadius: 5 }}><span>⚠</span><span>{c}</span></div>
                  ))}
                </div>
              )}
            </div>
          </window.Section>
        )}
      </div>

      {saveOpen && spec && (
        <SaveToDashboardDialog open={saveOpen} onClose={() => setSaveOpen(false)} spec={spec}
          sourceRequest={`Manual: ${spec.title}`} defaultGroup={targetGroup}
          onSaved={(dashId) => { setSaveOpen(false); onSavedToDashboard && onSavedToDashboard(dashId); }} />
      )}

      {csvOpen && spec && (
        <window.ExportDialog title={spec.title || 'Query result'}
          columns={exportColumns(spec, result && result.columns)}
          onClose={() => setCsvOpen(false)}
          onExport={(cols) => window.AtlasApi.analytics.exportCsv({ sql: spec.sql, title: spec.title, columns: cols || undefined })} />
      )}

      {drill && <DrillInspector spec={drill.spec} data={drill.data} selection={drill.selection} onClose={() => setDrill(null)} />}
    </div>
  );
};

// ── Top-level view ───────────────────────────────────────────────────────────
const AnalyticsView = ({ activeUser, currentRole, setView, goToView, builderPrefill, onPrefillConsumed, initialMode }) => {
  // Deep link: #/analytics/dashboards/<id>[?f=…] (written by DashboardCanvas)
  // re-opens that dashboard on mount — shared links and reloads land on the
  // dashboard, not the browser. Captured via initializers (one-shot).
  const [hashDashId] = useState(() => {
    const m = (window.location.hash || '').match(/^#\/analytics\/dashboards\/([^/?#]+)/);
    try { return m ? decodeURIComponent(m[1]) : null; } catch (_) { return null; }
  });
  const [mode, setMode] = useState(hashDashId ? 'dashboards' : (initialMode || 'builder'));     // builder | build | dashboards
  const [openDashId, setOpenDashId] = useState(hashDashId);
  const [targetDashId, setTargetDashId] = useState(null);  // builder → save here
  const [targetGroup, setTargetGroup] = useState(null);    // builder → new dashboard's group
  const [share, setShare] = useState(null);        // {dashboard, onSaved}
  const [viewEditor, setViewEditor] = useState(null);      // null | {group} | {prefill} — the Views builder

  // A prefill (from the drill drawer's "Open as table") opens the view builder
  // pre-loaded with the drill's source + filters; consume it so it fires once.
  useEffect(() => {
    if (builderPrefill) {
      setViewEditor({ prefill: builderPrefill });
      if (onPrefillConsumed) onPrefillConsumed();
    }
  }, [builderPrefill]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openView = goToView || ((id) => window.AtlasNav && window.AtlasNav.goToView && window.AtlasNav.goToView(id));

  const ModeSwitch = () => (
    <div style={{ display: 'inline-flex', padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
      {[['builder', 'Ask AI'], ['build', 'Build'], ['dashboards', 'Dashboards'], ['map', 'Map']].map(([k, l]) => (
        <button key={k} onClick={() => { setMode(k); setOpenDashId(null); }} style={{
          padding: '4px 12px', border: 'none', cursor: 'pointer', borderRadius: 5, fontSize: 12,
          fontWeight: mode === k ? 600 : 500,
          background: mode === k ? 'var(--surface)' : 'transparent',
          color: mode === k ? 'var(--text)' : 'var(--text-3)',
          boxShadow: mode === k ? 'var(--shadow-sm)' : 'none',
        }}>{l}</button>
      ))}
    </div>
  );

  if (viewEditor) {
    return <window.ViewEditor initial={null} prefill={viewEditor.prefill || null} initialGroup={viewEditor.group || null}
      onCancel={() => setViewEditor(null)}
      onSaved={(v) => { setViewEditor(null); if (v && v.id) openView(v.id); }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <window.PageHeader breadcrumb={['Workspace', 'Analytics']} title="Analytics"
        actions={<ModeSwitch />} />

      {mode === 'builder' && (
        <Builder targetDashboardId={targetDashId} targetGroup={targetGroup}
          onSavedToDashboard={(dashId) => { setTargetDashId(null); setTargetGroup(null); setMode('dashboards'); setOpenDashId(dashId); }}
          onOpenDashboard={(dashId) => { setMode('dashboards'); setOpenDashId(dashId); }} />
      )}

      {mode === 'build' && (
        <ManualBuilder targetGroup={targetGroup}
          onSavedToDashboard={(dashId) => { setTargetGroup(null); setMode('dashboards'); setOpenDashId(dashId); }} />
      )}

      {mode === 'map' && <window.TerritoryMap activeUser={activeUser} />}

      {mode === 'dashboards' && !openDashId && (
        <DashboardsBrowser onOpen={setOpenDashId}
          onNew={(group) => { setTargetDashId(null); setTargetGroup(group || null); setMode('builder'); }}
          onOpenView={openView}
          onNewView={(group) => setViewEditor({ group: group || null })} />
      )}

      {mode === 'dashboards' && openDashId && (
        <DashboardCanvas dashId={openDashId} onBack={() => setOpenDashId(null)}
          onAddWidget={(dashId) => { setTargetDashId(dashId); setMode('builder'); }}
          onShare={(dashboard, onSaved) => setShare({ dashboard, onSaved })} />
      )}

      {share && (
        <ShareDialog open onClose={() => setShare(null)} dashboard={share.dashboard}
          onSaved={() => { const cb = share.onSaved; setShare(null); cb && cb(); }} />
      )}
    </div>
  );
};

// DrillInspector + FilterValueInput are shared with the Views page,
// SaveToDashboardDialog with the opened-view header's "Add to dashboard", and
// DashboardCanvas with Home's pinned-dashboard section (embedded mode)
// (esbuild modules are isolated — cross-file symbols go through window.*).
Object.assign(window, { AnalyticsView, DrillInspector, FilterValueInput, SaveToDashboardDialog, DashboardCanvas });
